import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import { getCached, setCached, invalidate } from '../lib/queryCache'
import * as XLSX from 'xlsx'
import { buildInventory, buildMaterialsPolicyMap, getMaterialStockStatus as sharedGetMaterialStockStatus, SYSTEM_DEFAULT_CRITICAL_PCT, SYSTEM_DEFAULT_REORDER_PCT } from '../lib/inventorySSOT'

const fmt = (n) => n ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'
const fmtM = (n) => { const v = Number(n||0); if(v>=1e6) return (v/1e6).toFixed(2)+'M'; if(v>=1e3) return (v/1e3).toFixed(1)+'K'; return v.toFixed(2) }

const XIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>)
const CheckIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4"><polyline points="20 6 9 17 4 12"/></svg>)
const PlusIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>)
const SearchIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/></svg>)
const TrashIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" strokeLinecap="round" strokeLinejoin="round"/></svg>)
const DownloadIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round"/></svg>)
const UploadIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round"/></svg>)

const Toast = ({ message, type, onClose }) => (
  <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium ${type==='success'?'bg-[#1A7A4A] text-white':'bg-red-600 text-white'}`}>
    {type==='success'?<CheckIcon/>:<XIcon/>}<span>{message}</span>
    <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100"><XIcon/></button>
  </div>
)

// ── Receipt Modal (مع PO) ─────────────────────────────────────────────────────
const ReceiptModal = ({ project, poLine, po, costCodes, receipts, onSave, onClose }) => {
  const [form, setForm] = useState({
    material: poLine?.description || '',
    unit: poLine?.unit || '',
    qty_received: '',
    unit_rate: poLine?.unit_rate || '',
    total_exc_vat: '',
    total_inc_vat: '',
    received_date: new Date().toISOString().split('T')[0],
    period_month: new Date().toISOString().slice(0,7)+'-01',
    delivery_note_no: '',
    received_by: '',
    inventory_ref: poLine?.ref_code || '',
    ref_code: poLine?.ref_code || '',
    cost_code_id: poLine?.cost_code_id || '',
    po_id: po?.id || null,
    po_line_item_id: poLine?.id || null,
    material_id: null,
  })

  // Auto-fetch material_id from Material Master by ref_code
  useEffect(() => {
    if (!form.ref_code || !project?.id) return
    supabase.from('materials').select('id,description,unit')
      .eq('project_id', project.id)
      .eq('material_code', form.ref_code.trim().toUpperCase())
      .single()
      .then(({ data }) => {
        if (data) {
          setForm(f => ({
            ...f,
            material_id: data.id,
            material: f.material || data.description || '',
            unit: f.unit || data.unit || '',
          }))
        }
      })
  }, [form.ref_code, project?.id]) // eslint-disable-line

  const h = (k,v) => setForm(f => {
    const updated = { ...f, [k]: v }
    if (k === 'qty_received' || k === 'unit_rate') {
      const qty = parseFloat(k==='qty_received'?v:f.qty_received)||0
      const rate = parseFloat(k==='unit_rate'?v:f.unit_rate)||0
      updated.total_exc_vat = (qty*rate).toFixed(2)
      updated.total_inc_vat = (qty*rate*1.15).toFixed(2)
    }
    return updated
  })

  const [confirmData, setConfirmData] = useState(null) // {over, totalAfter, poQty}

  const handleReceive = () => {
    const qtyToReceive = parseFloat(form.qty_received)||0
    if (qtyToReceive <= 0) return alert('Qty must be greater than 0')

    if (poLine) {
      const poQty = parseFloat(poLine.qty)||0
      const totalReceivedSoFar = (receipts||[])
        .filter(r => r.po_line_item_id === poLine.id)
        .reduce((s,r) => s + (parseFloat(r.qty_received)||0), 0)
      const totalAfter = totalReceivedSoFar + qtyToReceive

      if (poQty > 0 && totalAfter > poQty) {
        setConfirmData({ over: (totalAfter - poQty).toFixed(2), totalAfter: totalAfter.toFixed(2), poQty: poQty.toFixed(2), unit: form.unit||'' })
        return
      }
    }
    onSave(form)
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-[#1C1A18] mb-1">New Store Receipt (GRN)</h3>
        {po && <p className="text-xs text-[#1A7A4A] font-medium">PO: {po.po_number} · {po.vendor}</p>}
        {poLine && (
          <div className="mt-2 mb-5 flex items-center gap-3 p-3 bg-[#F5F1EA] rounded-xl text-xs">
            <div><span className="text-[#9CA3AF]">PO Qty:</span> <span className="font-semibold text-[#2E2C29]">{fmt(poLine.qty)}</span></div>
            <div className="w-px h-4 bg-[#EDE7DA]"/>
            <div><span className="text-[#9CA3AF]">Unit Rate:</span> <span className="font-semibold text-[#2E2C29]">{fmt(poLine.unit_rate)}</span></div>
          </div>
        )}
        {!po && !poLine && <div className="mb-5"/>}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Material Description *</label>
            <input value={form.material} onChange={e=>h('material',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Ref Code (Material Code)</label>
            <input value={form.ref_code} onChange={e=>h('ref_code',e.target.value)} placeholder="e.g. STL-12"
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Unit</label>
            <input value={form.unit} onChange={e=>h('unit',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Qty Received</label>
            <input type="number" value={form.qty_received} onChange={e=>h('qty_received',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Unit Rate (Exc.VAT)</label>
            <input type="number" value={form.unit_rate} onChange={e=>h('unit_rate',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Total Exc.VAT</label>
            <input type="number" value={form.total_exc_vat} onChange={e=>h('total_exc_vat',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm bg-[#F5F1EA] focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Total Inc.VAT</label>
            <input type="number" value={form.total_inc_vat} onChange={e=>h('total_inc_vat',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm bg-[#F5F1EA] focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Received Date</label>
            <input type="date" value={form.received_date} onChange={e=>h('received_date',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Period Month</label>
            <input type="month" value={form.period_month?.slice(0,7)} onChange={e=>h('period_month',e.target.value+'-01')}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Delivery Note No.</label>
            <input value={form.delivery_note_no} onChange={e=>h('delivery_note_no',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Received By</label>
            <input value={form.received_by} onChange={e=>h('received_by',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Cost Code</label>
            <select value={form.cost_code_id} onChange={e=>h('cost_code_id',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]">
              <option value="">— Select —</option>
              {costCodes.map(c=><option key={c.id} value={c.id}>{c.code} — {c.description}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-[#E5E7EB] rounded-xl text-sm font-medium text-[#6B7280] hover:bg-[#FAFAFA]">Cancel</button>
          <button onClick={handleReceive} className="flex-1 px-4 py-2 bg-[#1C1A18] rounded-xl text-sm font-medium text-white hover:bg-[#2E2C29]">Save Receipt</button>
        </div>

        {/* ── Inline Confirm Overlay ── */}
        {confirmData && (
          <div className="absolute inset-0 bg-black/60 rounded-2xl flex items-center justify-center z-50 p-6">
            <div className="bg-white rounded-xl p-6 shadow-xl w-full max-w-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-600 text-xl">⚠</span>
                </div>
                <div className="text-sm font-semibold text-[#1C1A18]">Receipt Exceeds PO Quantity</div>
              </div>
              <p className="text-sm text-[#6B7280] mb-1">Total received will be <span className="font-bold text-[#DC2626]">{confirmData.totalAfter} {confirmData.unit}</span></p>
              <p className="text-sm text-[#6B7280] mb-1">PO Quantity: <span className="font-semibold">{confirmData.poQty} {confirmData.unit}</span></p>
              <p className="text-sm text-[#6B7280] mb-4">Over by: <span className="font-bold text-[#DC2626]">{confirmData.over} {confirmData.unit}</span></p>
              <p className="text-sm font-medium text-[#1C1A18] mb-4">Do you want to proceed?</p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmData(null)}
                  className="flex-1 px-4 py-2 border border-[#E5E7EB] rounded-xl text-sm font-medium text-[#6B7280] hover:bg-[#FAFAFA]">Cancel</button>
                <button onClick={() => { setConfirmData(null); onSave(form) }}
                  className="flex-1 px-4 py-2 bg-[#DC2626] rounded-xl text-sm font-medium text-white hover:bg-[#B91C1C]">Proceed Anyway</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Issue Modal (مع PO) ───────────────────────────────────────────────────────
const IssueModal = ({ project, poLine, po, costCodes, subCodesList, receipts, issues, onSave, onClose }) => {
  const [form, setForm] = useState({
    material: poLine?.description || '',
    unit: poLine?.unit || '',
    qty_issued: '',
    unit_rate: poLine?.unit_rate || '',
    issued_date: new Date().toISOString().split('T')[0],
    period_month: new Date().toISOString().slice(0,7)+'-01',
    sub_code: poLine?.sub_code || po?.sub_code || '',
    issued_by: '',
    inventory_ref: poLine?.ref_code || '',
    ref_code: poLine?.ref_code || '',
    cost_code_id: poLine?.cost_code_id || '',
    po_id: po?.id || null,
    po_line_item_id: poLine?.id || null,
    material_id: null,
  })

  // Auto-fetch material_id from Material Master by ref_code
  useEffect(() => {
    if (!form.ref_code || !project?.id) return
    supabase.from('materials').select('id')
      .eq('project_id', project.id)
      .eq('material_code', form.ref_code.trim().toUpperCase())
      .single()
      .then(({ data }) => { if (data) setForm(f => ({ ...f, material_id: data.id })) })
  }, [form.ref_code, project?.id]) // eslint-disable-line
  const [confirmData, setConfirmData] = useState(null) // {over, totalAfter, totalReceived}
  const h = (k,v) => setForm(f=>({...f,[k]:v}))

  // Sub Code selection drives everything else — Cost Code (and Resource Code, if any)
  // are derived automatically, never entered manually.
  const selectedSub = (subCodesList||[]).find(s => s.code === form.sub_code)
  function handleSubCodeChange(code) {
    const sub = (subCodesList||[]).find(s => s.code === code)
    setForm(f => ({ ...f, sub_code: code, cost_code_id: sub?.costCodeId || f.cost_code_id }))
  }

  const handleIssue = () => {
    if (!form.material) return alert('Material is required')
    if (!form.sub_code) return alert('Sub Code is required')
    const qtyToIssue = parseFloat(form.qty_issued)||0
    if (qtyToIssue <= 0) return alert('Qty must be greater than 0')

    const refKey = (form.ref_code || form.material || '').trim().toUpperCase()
    const poId = form.po_id

    const totalReceived = (receipts||[])
      .filter(r => { const k = (r.ref_code||r.material||'').trim().toUpperCase(); return poId ? r.po_id === poId && k === refKey : k === refKey })
      .reduce((s,r) => s + (parseFloat(r.qty_received)||0), 0)
    const totalIssuedSoFar = (issues||[])
      .filter(i => { const k = (i.ref_code||i.material||'').trim().toUpperCase(); return poId ? i.po_id === poId && k === refKey : k === refKey })
      .reduce((s,i) => s + (parseFloat(i.qty_issued)||0), 0)
    const totalAfter = totalIssuedSoFar + qtyToIssue


    if (totalAfter > totalReceived) {
      setConfirmData({ over: (totalAfter - totalReceived).toFixed(2), totalAfter: totalAfter.toFixed(2), totalReceived: totalReceived.toFixed(2), unit: form.unit||'' })
    } else {
      onSave(form)
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-[#1C1A18] mb-1">New Store Issue</h3>
        {po && <p className="text-xs text-[#1A7A4A] font-medium">PO: {po.po_number} · {po.vendor}</p>}
        {poLine && (
          <div className="mt-2 mb-5 flex items-center gap-3 p-3 bg-[#F5F1EA] rounded-xl text-xs">
            <div><span className="text-[#9CA3AF]">PO Qty:</span> <span className="font-semibold text-[#2E2C29]">{fmt(poLine.qty)}</span></div>
            <div className="w-px h-4 bg-[#EDE7DA]"/>
            <div><span className="text-[#9CA3AF]">Unit Rate:</span> <span className="font-semibold text-[#2E2C29]">{fmt(poLine.unit_rate)}</span></div>
          </div>
        )}
        {!po && !poLine && <div className="mb-5"/>}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Material *</label>
            <input value={form.material} onChange={e=>h('material',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Ref Code</label>
            <input value={form.ref_code} onChange={e=>h('ref_code',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Unit</label>
            <input value={form.unit} onChange={e=>h('unit',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Qty Issued</label>
            <input type="number" value={form.qty_issued} onChange={e=>h('qty_issued',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Unit Rate (Exc.VAT)</label>
            <input type="number" value={form.unit_rate} onChange={e=>h('unit_rate',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Issued Date</label>
            <input type="date" value={form.issued_date} onChange={e=>h('issued_date',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Period Month</label>
            <input type="month" value={form.period_month?.slice(0,7)} onChange={e=>h('period_month',e.target.value+'-01')}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Sub Code *</label>
            <select value={form.sub_code} onChange={e=>handleSubCodeChange(e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]">
              <option value="">— Select Sub Code —</option>
              {(subCodesList||[]).map(s=><option key={s.id} value={s.code}>{s.code} — {s.description}</option>)}
            </select>
            {/* Cost Code + Resource Code are derived automatically from the selected Sub Code — never entered manually */}
            {selectedSub && (
              <div className="mt-1.5 space-y-0.5">
                <p className="text-xs text-[#6B7280]">
                  Cost Code: <span className="font-mono font-semibold text-[#1A7A4A]">{selectedSub.costCode}</span> — {selectedSub.costCodeDescription}
                </p>
                {selectedSub.resourceCode && (
                  <p className="text-xs text-purple-700">
                    Resource Code: <span className="font-mono font-semibold">{selectedSub.resourceCode}</span> — {selectedSub.resourceCodeDescription}
                  </p>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Issued By</label>
            <input value={form.issued_by} onChange={e=>h('issued_by',e.target.value)}
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-[#E5E7EB] rounded-xl text-sm font-medium text-[#6B7280] hover:bg-[#FAFAFA]">Cancel</button>
          <button onClick={handleIssue} className="flex-1 px-4 py-2 bg-[#1A7A4A] rounded-xl text-sm font-medium text-white hover:bg-[#15693F]">Issue Material</button>
        </div>

        {/* ── Inline Confirm Overlay ── */}
        {confirmData && (
          <div className="absolute inset-0 bg-black/60 rounded-2xl flex items-center justify-center z-50 p-6">
            <div className="bg-white rounded-xl p-6 shadow-xl w-full max-w-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-600 text-xl">⚠</span>
                </div>
                <div className="text-sm font-semibold text-[#1C1A18]">Issue Exceeds Received</div>
              </div>
              <p className="text-sm text-[#6B7280] mb-1">Total issued will be <span className="font-bold text-[#DC2626]">{confirmData.totalAfter} {confirmData.unit}</span></p>
              <p className="text-sm text-[#6B7280] mb-1">Total received: <span className="font-semibold">{confirmData.totalReceived} {confirmData.unit}</span></p>
              <p className="text-sm text-[#6B7280] mb-4">Over by: <span className="font-bold text-[#DC2626]">{confirmData.over} {confirmData.unit}</span></p>
              <p className="text-sm font-medium text-[#1C1A18] mb-4">Do you want to proceed?</p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmData(null)}
                  className="flex-1 px-4 py-2 border border-[#E5E7EB] rounded-xl text-sm font-medium text-[#6B7280] hover:bg-[#FAFAFA]">Cancel</button>
                <button onClick={() => { setConfirmData(null); onSave(form) }}
                  className="flex-1 px-4 py-2 bg-[#DC2626] rounded-xl text-sm font-medium text-white hover:bg-[#B91C1C]">Proceed Anyway</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Bulk Receive/Issue Modal (كل بنود PO مرة واحدة) ────────────────────────────
const BulkModal = ({ project, po, bulkType, poLines, receipts, issues, costCodes, onSaveBulk, onClose }) => {
  const isReceive = bulkType === 'receipt'
  const today = new Date().toISOString().split('T')[0]
  const periodMonth = new Date().toISOString().slice(0,7)+'-01'

  const getStats = (line) => {
    const recv = receipts.filter(r => r.po_line_item_id === line.id || (r.po_id === po.id && r.ref_code && line.ref_code && r.ref_code.trim()===line.ref_code.trim()))
      .reduce((s,r)=>s+(parseFloat(r.qty_received)||0),0)
    const iss = issues.filter(i => i.po_line_item_id === line.id || (i.po_id === po.id && i.ref_code && line.ref_code && i.ref_code.trim()===line.ref_code.trim()))
      .reduce((s,i)=>s+(parseFloat(i.qty_issued)||0),0)
    return { received: recv, issued: iss }
  }

  // rows keyed by line.id: { qty: string }
  const [rows, setRows] = useState({})
  const [commonDate, setCommonDate] = useState(today)
  const [commonBy, setCommonBy] = useState('')
  const [confirmList, setConfirmList] = useState(null) // list of {line, over, totalAfter, limit} that exceed
  const [saving, setSaving] = useState(false)

  const setQty = (lineId, v) => setRows(r => ({ ...r, [lineId]: v }))

  const exportTemplate = () => {
    const data = poLines.map(l => {
      const stats = getStats(l)
      return {
        'Ref Code': l.ref_code||'',
        'Description': l.description||'',
        'Unit': l.unit||'',
        'PO Qty': l.qty||0,
        'Received So Far': stats.received,
        'Issued So Far': stats.issued,
        [isReceive?'Qty to Receive':'Qty to Issue']: '',
      }
    })
    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, isReceive?'Receive':'Issue')
    XLSX.writeFile(wb, `${po.po_number}_${isReceive?'receive':'issue'}_template.xlsx`)
  }

  const [importError, setImportError] = useState('')

  const importExcel = (file) => {
    setImportError('')
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type:'array' })
        const sheetRows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval:'' })
        const byRef = {}
        poLines.forEach(l => { if(l.ref_code) byRef[l.ref_code.trim().toUpperCase()] = l })
        const newRows = {}
        let matched = 0, skipped = 0
        sheetRows.forEach(row => {
          const refCode = String(row['Ref Code']||'').trim().toUpperCase()
          const qtyCol = isReceive ? 'Qty to Receive' : 'Qty to Issue'
          const qty = parseFloat(row[qtyCol])
          if (!refCode || !qty || qty<=0) return
          const line = byRef[refCode]
          if (!line) { skipped++; return }
          newRows[line.id] = String(qty)
          matched++
        })
        setRows(r => ({ ...r, ...newRows }))
        if (skipped>0) setImportError(`${matched} lines matched, ${skipped} ref codes not found in this PO`)
      } catch(err) { setImportError('Failed to read file: '+err.message) }
    }
    reader.readAsArrayBuffer(file)
  }

  const filledLines = poLines.filter(l => parseFloat(rows[l.id]) > 0)

  const buildPayload = (line, qty) => {
    if (isReceive) {
      return {
        project_id: project.id,
        material: line.description || '',
        unit: line.unit || null,
        qty_received: qty,
        unit_rate: parseFloat(line.unit_rate)||0,
        total_exc_vat: qty * (parseFloat(line.unit_rate)||0),
        total_inc_vat: qty * (parseFloat(line.unit_rate)||0) * 1.15,
        received_date: commonDate,
        period_month: periodMonth,
        delivery_note_no: null,
        received_by: commonBy||null,
        inventory_ref: line.ref_code||null,
        ref_code: line.ref_code||null,
        cost_code_id: line.cost_code_id||null,
        po_id: po.id,
        po_line_item_id: line.id,
        material_id: null,
      }
    }
    return {
      project_id: project.id,
      material: line.description || '',
      unit: line.unit || null,
      qty_issued: qty,
      unit_rate: parseFloat(line.unit_rate)||0,
      issued_date: commonDate,
      period_month: periodMonth,
      sub_code: line.sub_code || po.sub_code || null,
      issued_by: commonBy||null,
      inventory_ref: line.ref_code||null,
      ref_code: line.ref_code||null,
      cost_code_id: line.cost_code_id||null,
      po_id: po.id,
      po_line_item_id: line.id,
      material_id: null,
    }
  }

  const handleSaveAll = () => {
    if (filledLines.length === 0) return alert('Enter at least one quantity')
    if (!isReceive && filledLines.some(l => !l.cost_code_id)) return alert('Some lines are missing a Cost Code — cannot issue without it')

    // Check over-limit for each filled line
    const exceeding = []
    filledLines.forEach(line => {
      const qty = parseFloat(rows[line.id])||0
      const stats = getStats(line)
      if (isReceive) {
        const poQty = parseFloat(line.qty)||0
        const totalAfter = stats.received + qty
        if (poQty > 0 && totalAfter > poQty) exceeding.push({ line, over: (totalAfter-poQty).toFixed(2), totalAfter: totalAfter.toFixed(2), limit: poQty.toFixed(2), limitLabel:'PO Qty' })
      } else {
        const totalAfter = stats.issued + qty
        if (totalAfter > stats.received) exceeding.push({ line, over: (totalAfter-stats.received).toFixed(2), totalAfter: totalAfter.toFixed(2), limit: stats.received.toFixed(2), limitLabel:'Received' })
      }
    })

    if (exceeding.length > 0) { setConfirmList(exceeding); return }
    doSave()
  }

  const doSave = async () => {
    setSaving(true)
    const payloads = filledLines.map(line => buildPayload(line, parseFloat(rows[line.id])||0))
    await onSaveBulk(payloads, isReceive)
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-[#1C1A18] mb-1">Bulk {isReceive?'Receive':'Issue'} — {po.po_number}</h3>
        <p className="text-xs text-[#1A7A4A] font-medium mb-4">{po.vendor} · Fill quantity for each line, or download the Excel template, fill it, and upload it back</p>

        <div className="flex items-center gap-2 mb-4">
          <button onClick={exportTemplate} className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-lg text-[#6B7280] hover:bg-[#FAFAFA] font-medium">
            ⬇ Download Excel Template
          </button>
          <label className="px-3 py-1.5 text-xs border border-[#1A7A4A] rounded-lg text-[#1A7A4A] hover:bg-[#F0FDF4] font-medium cursor-pointer">
            ⬆ Upload Filled Excel
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={e=>{ if(e.target.files[0]) importExcel(e.target.files[0]); e.target.value='' }}/>
          </label>
          {importError && <span className="text-xs text-amber-600">{importError}</span>}
        </div>

        <div className="flex gap-4 mb-4 p-3 bg-[#F5F1EA] rounded-xl">
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">{isReceive?'Received':'Issued'} Date</label>
            <input type="date" value={commonDate} onChange={e=>setCommonDate(e.target.value)}
              className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">{isReceive?'Received By':'Issued By'}</label>
            <input value={commonBy} onChange={e=>setCommonBy(e.target.value)} placeholder="Name"
              className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
          </div>
          <div className="flex items-end ml-auto">
            <span className="text-xs text-[#6B7280]">{filledLines.length} line{filledLines.length!==1?'s':''} will be {isReceive?'received':'issued'}</span>
          </div>
        </div>

        <div className="overflow-x-auto border border-[#E5E7EB] rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] sticky top-0">
              <tr>
                {['Ref Code','Description','Unit','PO Qty','Received','Issued','Balance', isReceive?'Qty to Receive':'Qty to Issue'].map(h=>(
                  <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-[#6B7280] uppercase whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {poLines.map(line => {
                const stats = getStats(line)
                const balance = stats.received - stats.issued
                const cap = isReceive ? (parseFloat(line.qty)||0) - stats.received : balance
                return (
                  <tr key={line.id} className="hover:bg-[#FAFAFA]">
                    <td className="px-3 py-2 font-mono text-xs text-[#1A7A4A]">{line.ref_code||'—'}</td>
                    <td className="px-3 py-2 text-[#2E2C29] max-w-xs">{line.description}</td>
                    <td className="px-3 py-2 text-xs text-[#6B7280]">{line.unit||'—'}</td>
                    <td className="px-3 py-2 text-right text-xs font-medium">{fmt(line.qty)}</td>
                    <td className="px-3 py-2 text-right text-xs text-[#1A7A4A] font-medium">{fmt(stats.received)}</td>
                    <td className="px-3 py-2 text-right text-xs text-[#1A7A4A] font-medium">{fmt(stats.issued)}</td>
                    <td className="px-3 py-2 text-right text-xs">
                      <span className={balance<0?'text-red-500 font-medium':'text-[#6B7280]'}>{fmt(balance)}</span>
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" value={rows[line.id]||''} onChange={e=>setQty(line.id, e.target.value)}
                        placeholder={cap>0?`max ${fmt(cap)}`:'0'}
                        className="w-28 border border-[#E5E7EB] rounded-lg px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
                    </td>
                  </tr>
                )
              })}
              {poLines.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-[#9CA3AF]">No line items for this PO</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-[#E5E7EB] rounded-xl text-sm font-medium text-[#6B7280] hover:bg-[#FAFAFA]">Cancel</button>
          <button onClick={handleSaveAll} disabled={saving || filledLines.length===0}
            className="flex-1 px-4 py-2 bg-[#1C1A18] rounded-xl text-sm font-medium text-white hover:bg-[#2E2C29] disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? 'Saving...' : `Save All (${filledLines.length})`}
          </button>
        </div>

        {/* ── Bulk Confirm Overlay ── */}
        {confirmList && (
          <div className="absolute inset-0 bg-black/60 rounded-2xl flex items-center justify-center z-50 p-6">
            <div className="bg-white rounded-xl p-6 shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-600 text-xl">⚠</span>
                </div>
                <div className="text-sm font-semibold text-[#1C1A18]">{confirmList.length} line{confirmList.length!==1?'s':''} exceed limit</div>
              </div>
              <div className="space-y-2 mb-4">
                {confirmList.map(c => (
                  <div key={c.line.id} className="text-xs bg-[#F5F1EA] rounded-lg p-2">
                    <span className="font-medium text-[#1C1A18]">{c.line.ref_code||c.line.description}</span>
                    <span className="text-[#6B7280]"> — will be {c.totalAfter}, {c.limitLabel}: {c.limit}, over by </span>
                    <span className="font-bold text-[#DC2626]">{c.over}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm font-medium text-[#1C1A18] mb-4">Do you want to proceed with all {filledLines.length} lines anyway?</p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmList(null)}
                  className="flex-1 px-4 py-2 border border-[#E5E7EB] rounded-xl text-sm font-medium text-[#6B7280] hover:bg-[#FAFAFA]">Cancel</button>
                <button onClick={() => { setConfirmList(null); doSave() }}
                  className="flex-1 px-4 py-2 bg-[#DC2626] rounded-xl text-sm font-medium text-white hover:bg-[#B91C1C]">Proceed Anyway</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Fetch all rows for a query, bypassing Supabase's default 1000-row cap.
// queryFn(from, to) must return a Supabase query builder (already filtered) - we only add .range().
// Duplicated here from CashOutForecast.jsx / Finance.jsx per explicit decision (2026-08-12):
// keep this fix minimal-risk and standalone; extracting a shared /lib helper is deferred
// as a separate technical-debt item (see project TD-2), not bundled into this fix.
async function fetchAllRows(queryFn) {
  const PAGE = 1000
  let from = 0
  let all = []
  while (true) {
    const { data } = await queryFn(from, from + PAGE - 1)
    if (!data?.length) break
    all = all.concat(data)
    if (data.length < PAGE) break
    from += PAGE
  }
  return all
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Store({ initProject }) {
  const [projects, setProjects] = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  useEffect(() => { if (initProject) setSelectedProject(initProject) }, [initProject?.id])
  // Real authorization source -- can() mirrors has_permission() exactly,
  // same hook pattern as IPC/PO/PR/Subcontract/Inventory/RFQ.
  const { can } = useProjectAuth(selectedProject?.id)
  const [issueSubCode, setIssueSubCode] = useState('all')
  const [activeTab, setActiveTab] = useState('po') // po | receipts | issues | inventory
  const [pos, setPOs] = useState([])
  const [selectedPO, setSelectedPO] = useState(null)
  const [poLines, setPOLines] = useState([])
  const [receipts, setReceipts] = useState([])
  const [issues, setIssues] = useState([])
  const [issuesTotalAmount, setIssuesTotalAmount] = useState(0)
  const [receiptsTotalAmount, setReceiptsTotalAmount] = useState(0)
  const [costCodes, setCostCodes] = useState([])
  const [materialsMaster, setMaterialsMaster] = useState([]) // Stock Policy source (materials table): ref_code == material_code
  const [transferOutLines, setTransferOutLines] = useState([]) // SSOT correction (2026-08-13): same source InventoryManagement.jsx uses
  const [transferInLines, setTransferInLines] = useState([])
  const [subCodesList, setSubCodesList] = useState([]) // level=2 with resolved Cost Code + Resource Code ancestry
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [poSearch, setPOSearch] = useState('')
  const [modal, setModal] = useState(null) // null | {type:'receipt'|'issue'|'bulk', poLine, po, bulkType}
  const [uploadPreview, setUploadPreview] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [issuesUploadPreview, setIssuesUploadPreview] = useState(null)
  const [issuesSkippedRows, setIssuesSkippedRows] = useState([]) // F-15: rows dropped during parse, with reason
  const [issuesNoCostCodeCount, setIssuesNoCostCodeCount] = useState(0) // Cost Code warning count
  const [issuesLikelyDuplicates, setIssuesLikelyDuplicates] = useState([]) // F-13: possible duplicate rows
  const [issuesImportConfirmed, setIssuesImportConfirmed] = useState(false) // user must confirm past warnings (WARN, not BLOCK)
  const [receiptsSkippedRows, setReceiptsSkippedRows] = useState([]) // F-15 for Receipts
  const [receiptsLikelyDuplicates, setReceiptsLikelyDuplicates] = useState([]) // F-13 for Receipts
  const [receiptsImportConfirmed, setReceiptsImportConfirmed] = useState(false)
  const [inventorySearch, setInventorySearch] = useState('')
  const [inventoryStatusFilter, setInventoryStatusFilter] = useState('all')
  const [inventoryBalanceFilter, setInventoryBalanceFilter] = useState({ op: '', val: '' })
  const [issuesUploading, setIssuesUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [toast, setToast] = useState(null)
  const [selected, setSelected] = useState(new Set())

  const showToast = (msg, type='success') => { setToast({message:msg,type}); setTimeout(()=>setToast(null),4000) }

  const [bulkDate, setBulkDate] = useState('')
  const [bulkUpdating, setBulkUpdating] = useState(false)
  const applyBulkDate = async (ids) => {
    if (!bulkDate) return showToast('Select a date first','error')
    if (!window.confirm('Update issued_date for ' + ids.length + ' records?')) return
    setBulkUpdating(true)
    // Batch to avoid URL length limit
    const BATCH = 100
    let hasError = false
    for (let i = 0; i < ids.length; i += BATCH) {
      const { error } = await supabase.from('store_issues').update({ issued_date: bulkDate }).in('id', ids.slice(i, i+BATCH))
      if (error) { hasError = true; showToast('Error: '+error.message,'error'); break }
    }
    if (!hasError) { showToast('Updated '+ids.length+' records ✓'); loadData(selectedProject.id) }
    setBulkUpdating(false)
  }

  useEffect(() => {
    const _pc = getCached('projects_list_c')
    if (_pc) setProjects(_pc)
    else supabase.from('projects').select('id,name,currency,organizations(name)').order('name').then(({data})=>{ setProjects(data||[]); if(data) setCached('projects_list_c',data) })
  }, [])

  const loadData = useCallback(async (projectId) => {
    setLoading(true)
    const [poRes, receiptsAll, issuesAll, allIssuesRes, allReceiptsRes, ccRes, materialsRes, transferOutRes, transferInRes] = await Promise.all([
      supabase.from('purchase_orders').select('id,po_number,vendor,po_type,cost_type,sub_code,total_exc_vat,total_inc_vat,vat_pct,status,issue_date,cost_code_id,cost_codes(code)').eq('project_id',projectId).eq('po_type','standard').order('po_number',{ascending:false}),
      // F-01 fix (2026-08-12): was .range(0,4999), silently dropping older rows past
      // the 5000th for any project that outgrew the cap while KPI totals (RPC-based,
      // below) stayed correct — causing a silent mismatch between the KPI cards and
      // every other Store screen/report. Now paginated to fetch the full table.
      // .order('id') added as a stable secondary sort so no row is skipped or
      // duplicated across pages when multiple rows share the same date.
      fetchAllRows((from, to) =>
        supabase.from('store_receipts').select('*,cost_codes(code),purchase_orders(po_number)')
          .eq('project_id',projectId).order('received_date',{ascending:false}).order('id',{ascending:true}).range(from,to)),
      fetchAllRows((from, to) =>
        supabase.from('store_issues').select('id,po_id,ref_code,material,sub_code,unit,qty_issued,unit_rate,amount,issued_date,cost_code_id,site_ref,inventory_ref,cost_codes(code)')
          .eq('project_id',projectId).order('issued_date',{ascending:false}).order('id',{ascending:true}).range(from,to)),
      supabase.rpc('sum_store_issues', { p_project_id: projectId }),
      supabase.rpc('sum_store_receipts', { p_project_id: projectId }),
      supabase.from('cost_codes').select('id,code,description,level,parent_id').eq('project_id',projectId).order('code'),
      // Stock Policy source (F-06 fix, 2026-08-12): materials.material_code is
      // matched against Store's ref_code (business decision: ref_code IS the
      // material identity — no separate FK migration).
      supabase.from('materials').select('material_code,threshold_type,critical_level,reorder_level').eq('project_id',projectId),
      // SSOT correction (2026-08-13): Store.jsx was silently missing this third
      // data source entirely, causing it to diverge from InventoryManagement.jsx
      // for any material touched by an approved Transfer. Same query shape,
      // same 'approved' status filter, as InventoryManagement.jsx's
      // loadProjectInventoryContext() in transferService.js -- both screens now
      // feed the identical three sources into the identical buildInventory().
      supabase.from('inventory_transfer_lines')
        .select('ref_code,material,unit,qty,source_wac,inventory_transfers!inner(from_project_id,status,transfer_no,transfer_date)')
        .eq('inventory_transfers.from_project_id', projectId)
        .in('inventory_transfers.status', ['approved', 'received']),
      supabase.from('inventory_transfer_lines')
        .select('ref_code,material,unit,qty,source_wac,inventory_transfers!inner(to_project_id,status,transfer_no,transfer_date)')
        .eq('inventory_transfers.to_project_id', projectId)
        .eq('inventory_transfers.status', 'received'),
    ])
    setPOs(poRes.data||[])
    setReceipts(receiptsAll||[])
    setIssues(issuesAll||[])
    setIssuesTotalAmount(allIssuesRes.data || 0)
    setReceiptsTotalAmount(allReceiptsRes.data || 0)
    setCostCodes(ccRes.data||[])
    setMaterialsMaster(materialsRes.data||[])
    setTransferOutLines(transferOutRes.data||[])
    setTransferInLines(transferInRes.data||[])

    // Build Sub Codes list (level 2) with resolved ancestry: parent Cost Code (level 1),
    // and if the parent is actually a Resource Code (level 4), resolve its own parent too.
    const all = ccRes.data || []
    const byId = {}
    all.forEach(c => { byId[c.id] = c })
    const subs = all.filter(c => parseInt(c.level) === 2).map(sub => {
      const parent = byId[sub.parent_id]
      let costCode = null, resourceCode = null
      if (parent && parseInt(parent.level) === 4) {
        resourceCode = parent
        costCode = byId[parent.parent_id] || null
      } else if (parent && parseInt(parent.level) === 1) {
        costCode = parent
      }
      return {
        ...sub,
        costCodeId: costCode?.id || null,
        costCode: costCode?.code || '—',
        costCodeDescription: costCode?.description || '',
        resourceCode: resourceCode?.code || null,
        resourceCodeDescription: resourceCode?.description || null,
      }
    })
    setSubCodesList(subs)
    setLoading(false)
  }, [])

  useEffect(() => { if (selectedProject?.id) loadData(selectedProject.id) }, [selectedProject?.id, loadData])

  const handleProjectSelect = (p) => {
    setSelectedProject(p); setPOs([]); setReceipts([]); setIssues([]); setIssuesTotalAmount(0); setReceiptsTotalAmount(0)
    setSelectedPO(null); setPOLines([]); setSelected(new Set()); setSearch('')
    setTransferOutLines([]); setTransferInLines([])
    if(p) loadData(p.id)
  }

  const handlePOSelect = async (po) => {
    setSelectedPO(po)
    const { data } = await supabase.from('po_line_items').select('*,cost_codes(code,description)').eq('po_id',po.id).order('created_at')
    setPOLines(data||[])
  }

  // Stats
  const totalReceived = receiptsTotalAmount || receipts.reduce((s,r)=>s+(parseFloat(r.total_exc_vat)||0),0)
  const totalIssued = issuesTotalAmount || issues.reduce((s,i)=>s+(parseFloat(i.amount)||0),0)

  // Stock Policy lookup: ref_code (Store) == material_code (Material Master),
  // per the business decision (2026-08-12) that ref_code IS the material identity.
  // Uses the shared inventorySSOT.js builder -- identical logic, single source.
  const materialsPolicyMap = buildMaterialsPolicyMap(materialsMaster)

  // F-06 fix (2026-08-12), refactored 2026-08-13: this now calls the shared
  // inventorySSOT.js module (the single Inventory calculation engine) instead
  // of defining the logic locally.
  const getMaterialStockStatus = (item) => sharedGetMaterialStockStatus(item, materialsPolicyMap)

  // Inventory by ref_code — delegates to the shared inventorySSOT.js
  // buildInventory() (the single Inventory SSOT). SSOT correction
  // (2026-08-13): Store.jsx now passes transferOutLines/transferInLines --
  // the exact same three-source call InventoryManagement.jsx makes. This is
  // the fix for the divergence found during testing: a material that exists
  // in a project only via an approved Transfer-In was invisible here because
  // this call previously omitted the third data source entirely. No second
  // calculation engine was created -- this is the same function, same file,
  // now simply given the data it already accepted as an optional parameter.
  const inventory = buildInventory(receipts, issues, materialsPolicyMap, transferOutLines, transferInLines)


  // Total Stock Value (Weighted Average Cost method) — sum of each material's
  // own balance × its own weighted average rate, read from the single SSOT above.
  const totalStockValue = inventory.reduce((s, i) => s + i.stockValue, 0)

  // PO line stats
  const getPOLineStats = (lineId, poId, refCode) => {
    // Match by po_line_item_id (manual entry) OR by po_id+ref_code (bulk upload)
    const recv = receipts.filter(r =>
      r.po_line_item_id === lineId ||
      (r.po_id === poId && r.ref_code && refCode && r.ref_code.trim().toUpperCase() === refCode.trim().toUpperCase())
    ).reduce((s,r)=>s+(parseFloat(r.qty_received)||0),0)
    const iss = issues.filter(i =>
      i.po_line_item_id === lineId ||
      (i.po_id === poId && i.ref_code && refCode && i.ref_code.trim().toUpperCase() === refCode.trim().toUpperCase())
    ).reduce((s,i)=>s+(parseFloat(i.qty_issued)||0),0)
    return { received: recv, issued: iss }
  }

  const poMap = Object.fromEntries(pos.map(p => [p.id, p.po_number]))

  const filteredPOs = pos.filter(p=>
    !poSearch ||
    p.po_number?.toLowerCase().includes(poSearch.toLowerCase()) ||
    p.vendor?.toLowerCase().includes(poSearch.toLowerCase())
  )

  const filteredReceipts = receipts.filter(r=>!search||r.material?.toLowerCase().includes(search.toLowerCase())||r.ref_code?.toLowerCase().includes(search.toLowerCase())||r.purchase_orders?.po_number?.toLowerCase().includes(search.toLowerCase()))
  const filteredIssues = issues.filter(i=>{
    const matchSearch = !search||i.material?.toLowerCase().includes(search.toLowerCase())||i.ref_code?.toLowerCase().includes(search.toLowerCase())||i.site_ref?.toLowerCase().includes(search.toLowerCase())||i.sub_code?.toLowerCase().includes(search.toLowerCase())||(i.po_id&&poMap[i.po_id]?.toLowerCase().includes(search.toLowerCase()))
    return matchSearch
  })
  const filteredInventory = inventory.filter(i => {
    if (inventorySearch && !i.material?.toLowerCase().includes(inventorySearch.toLowerCase()) && !i.ref_code?.toLowerCase().includes(inventorySearch.toLowerCase())) return false
    if (inventoryStatusFilter !== 'all' && i.stockStatus !== inventoryStatusFilter) return false
    if (inventoryBalanceFilter.op && inventoryBalanceFilter.val !== '') {
      const val = parseFloat(inventoryBalanceFilter.val)
      if (!isNaN(val)) {
        if (inventoryBalanceFilter.op === 'lt' && !(i.balance < val)) return false
        if (inventoryBalanceFilter.op === 'gt' && !(i.balance > val)) return false
        if (inventoryBalanceFilter.op === 'eq' && !(i.balance === val)) return false
      }
    }
    return true
  })

  // ── Stock Alerts ──────────────────────────────────────────
  // F-06 fix (2026-08-12): reads the same unified item.stockStatus computed
  // once by getMaterialStockStatus() in the inventory array above — no
  // separate threshold logic here anymore (previously this had its own
  // independent 10%/25% definition, one of the four that used to disagree).
  const getStockAlert = (item) => {
    if (item.stockStatus === 'negative') return { level: 'negative', color: 'bg-red-100 text-red-700', label: 'Negative' }
    if (item.stockStatus === 'critical') return { level: 'critical', color: 'bg-red-100 text-red-700', label: 'Critical' }
    if (item.stockStatus === 'low') return { level: 'low', color: 'bg-amber-100 text-amber-700', label: 'Low Stock' }
    return null // in_stock and out (Out of Stock is normal - not an alert)
  }
  const criticalItems = inventory.filter(i => { const a = getStockAlert(i); return a?.level === 'critical' || a?.level === 'negative' })
  const lowItems = inventory.filter(i => getStockAlert(i)?.level === 'low')

  // ── Supplier Analysis ─────────────────────────────────────
  const supplierMap = {}
  receipts.forEach(r => {
    const po = pos.find(p => p.id === r.po_id)
    const vendor = po?.vendor || 'Unknown'
    if (!supplierMap[vendor]) supplierMap[vendor] = { vendor, totalValue: 0, totalQty: 0, materials: new Set(), poCount: new Set() }
    supplierMap[vendor].totalValue += parseFloat(r.total_exc_vat) || 0
    supplierMap[vendor].totalQty += parseFloat(r.qty_received) || 0
    supplierMap[vendor].materials.add(r.material)
    if (po) supplierMap[vendor].poCount.add(po.id)
  })
  const supplierAnalysis = Object.values(supplierMap)
    .map(s => ({ ...s, materials: s.materials.size, poCount: s.poCount.size }))
    .sort((a, b) => b.totalValue - a.totalValue)

  const handleSaveReceipt = async (form) => {
    if(!form.material) return showToast('Material is required','error')
    const { error } = await supabase.from('store_receipts').insert([{
      project_id: selectedProject.id,
      material: form.material, unit: form.unit||null,
      qty_received: parseFloat(form.qty_received)||0,
      unit_rate: parseFloat(form.unit_rate)||0,
      total_exc_vat: parseFloat(form.total_exc_vat)||0,
      total_inc_vat: parseFloat(form.total_inc_vat)||0,
      received_date: form.received_date||null,
      period_month: form.period_month||null,
      delivery_note_no: form.delivery_note_no||null,
      received_by: form.received_by||null,
      inventory_ref: form.ref_code||null,
      ref_code: form.ref_code||null,
      cost_code_id: form.cost_code_id||null,
      po_id: form.po_id||null,
      po_line_item_id: form.po_line_item_id||null,
      material_id: form.material_id||null,
    }])
    if(error) showToast('Failed: '+error.message,'error')
    else { showToast('Receipt saved ✓'); setModal(null); loadData(selectedProject.id) }
  }

  const handleSaveIssue = async (form) => {
    if(!form.material) return showToast('Material is required','error')
    if(!form.cost_code_id) return showToast('Cost Code is required','error')
    const qtyToIssue = parseFloat(form.qty_issued)||0
    if(qtyToIssue <= 0) return showToast('Qty must be greater than 0','error')
    const { error } = await supabase.from('store_issues').insert([{
      project_id: selectedProject.id,
      material: form.material, unit: form.unit||null,
      qty_issued: parseFloat(form.qty_issued)||0,
      unit_rate: parseFloat(form.unit_rate)||0,
      issued_date: form.issued_date||null,
      period_month: form.period_month||null,
      sub_code: form.sub_code||null,
      issued_by: form.issued_by||null,
      inventory_ref: form.ref_code||null,
      ref_code: form.ref_code||null,
      cost_code_id: form.cost_code_id||null,
      po_id: form.po_id||null,
      po_line_item_id: form.po_line_item_id||null,
      material_id: form.material_id||null,
    }])
    if(error) showToast('Failed: '+error.message,'error')
    else { showToast('Issued — Actual Cost updated ✓'); setModal(null); loadData(selectedProject.id) }
  }

  const handleSaveBulk = async (payloads, isReceive) => {
    const table = isReceive ? 'store_receipts' : 'store_issues'
    const { error } = await supabase.from(table).insert(payloads)
    if(error) showToast('Failed: '+error.message,'error')
    else { showToast(`${payloads.length} ${isReceive?'receipts':'issues'} saved ✓`); setModal(null); loadData(selectedProject.id) }
  }

  // Upload receipts from Excel
  const parseReceiptExcel = (file) => new Promise((resolve,reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result,{type:'array'})
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]],{defval:''})
        const scMap = {}; subCodesList.forEach(s=>{ scMap[s.code.trim().toLowerCase()]=s })
        const poMap = {}; pos.forEach(p=>{ poMap[p.po_number.trim().toLowerCase()]=p.id })
        let levelMismatch = 0
        const skippedRows = []
        const normalized = rows.map((row, idx) => {
          const l = {}
          Object.keys(row).forEach(k=>{ l[k.toLowerCase().replace(/[\s\/\.\-]+/g,'_')]=row[k] })
          const qty = parseFloat(l.qty_received||l.qty||0)||0
          const rate = parseFloat(l.unit_rate||l.rate||0)||0
          const excVat = parseFloat(l.total_exc_vat||0)||(qty*rate)
          const poNum = String(l.po_number||'').trim().toLowerCase()
          const rawCode = String(l.cost_code||l.sub_code||'').trim()
          const sc = rawCode ? scMap[rawCode.toLowerCase()] : null
          if (rawCode && !sc) {
            const anyMatch = costCodes.find(c=>c.code.trim().toLowerCase()===rawCode.toLowerCase())
            if (anyMatch) levelMismatch++
          }
          const material = String(l.material||l.description||'').trim()
          // F-15: record why a row would be skipped instead of silently dropping it
          if (!material) skippedRows.push({ row: idx+2, reason: 'Missing Material/Description' })
          return {
            project_id: selectedProject.id,
            material,
            unit: String(l.unit||'').trim()||null,
            qty_received: qty, unit_rate: rate,
            total_exc_vat: excVat,
            total_inc_vat: parseFloat(l.total_inc_vat||0)||(excVat*1.15),
            received_date: l.received_date||l.date||null,
            period_month: l.period_month||null,

            delivery_note_no: String(l.delivery_note_no||'').trim()||null,
            received_by: String(l.received_by||'').trim()||null,
            ref_code: String(l.ref_code||l.material_code||'').trim()||null,
            inventory_ref: String(l.ref_code||l.inventory_ref||'').trim()||null,
            cost_code_id: sc?.costCodeId || null,
            po_id: poNum ? (poMap[poNum]||null) : null,
          }
        }).filter(r=>r.material)
        if (levelMismatch > 0) showToast(`⚠ ${levelMismatch} صف فيه كود مش Sub Code فعلي في الشجرة`, 'error')
        resolve({ rows: normalized, skippedRows })
      } catch(err){reject(err)}
    }
    reader.onerror=reject; reader.readAsArrayBuffer(file)
  })

  // F-13 (2026-08-12): Duplicate import detection. No DB-level unique constraint
  // exists on store_receipts/store_issues beyond the primary key (confirmed via
  // direct schema query), so "duplicate" is an application-level judgment, not
  // an invented one: a new row is flagged when an EXISTING row already has the
  // same normalized ref_code + same qty + same date (+ same delivery_note_no
  // when both sides have one, for receipts). This is WARN only, never BLOCK --
  // two genuinely separate transactions could legitimately share these values.
  const findLikelyDuplicates = (newRows, existingRows, kind) => {
    const dupes = []
    newRows.forEach((nr, idx) => {
      const nrKey = (nr.ref_code || nr.material || '').trim().toUpperCase()
      if (!nrKey) return
      const match = existingRows.find(er => {
        const erKey = (er.ref_code || er.material || '').trim().toUpperCase()
        if (erKey !== nrKey) return false
        if (kind === 'receipt') {
          if (parseFloat(er.qty_received) !== parseFloat(nr.qty_received)) return false
          if ((er.received_date||'') !== (nr.received_date||'')) return false
          if (nr.delivery_note_no && er.delivery_note_no && nr.delivery_note_no !== er.delivery_note_no) return false
        } else {
          if (parseFloat(er.qty_issued) !== parseFloat(nr.qty_issued)) return false
          if ((er.issued_date||'') !== (nr.issued_date||'')) return false
        }
        return true
      })
      if (match) dupes.push({ row: idx+2, ref_code: nr.ref_code || nr.material, qty: kind==='receipt'?nr.qty_received:nr.qty_issued, date: kind==='receipt'?nr.received_date:nr.issued_date })
    })
    return dupes
  }

  const handleFileDrop = async (file) => {
    if(!file||!selectedProject) return
    const ext = file.name.split('.').pop().toLowerCase()
    if(!['xlsx','xls','csv'].includes(ext)) return showToast('Excel or CSV only','error')
    try {
      const { rows, skippedRows } = await parseReceiptExcel(file)
      if(!rows.length) return showToast('No valid rows','error')
      const likelyDuplicates = findLikelyDuplicates(rows, receipts, 'receipt')
      setUploadPreview(rows)
      setReceiptsSkippedRows(skippedRows)
      setReceiptsLikelyDuplicates(likelyDuplicates)
      setReceiptsImportConfirmed(false)
    }
    catch(e){ showToast('Parse error: '+e.message,'error') }
  }

  const handleUploadConfirm = async () => {
    setUploading(true)
    const { error } = await supabase.from('store_receipts').insert(uploadPreview)
    setUploading(false)
    if(error) showToast('Upload failed: '+error.message,'error')
    else { showToast(`${uploadPreview.length} receipts imported`); setUploadPreview(null); loadData(selectedProject.id) }
  }

  const downloadIssuesTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([{
      po_number: '2601435',
      ref_code: '78443000003',
      material: 'Supply and apply fire stop system',
      sub_code: 'Material.Zac',
      unit: 'LOT',
      qty_issued: 1,
      unit_rate: 55500,
      total_exc_vat: 55500,
      issued_date: '2026-04-16',
      period_month: '2026-04-01',
      issued_by: '',
      site_ref: '',
    }])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Issues')
    XLSX.writeFile(wb, 'store_issues_template.xlsx')
  }

  const parseIssuesExcel = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' })
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' })
        const scMap = {}
        subCodesList.forEach(s => {
          const key = s.code.trim().toLowerCase()
          scMap[key] = s
          scMap[key.replace(/[\s\.\-]/g, '')] = s
        })
        const resolveSubCode = (raw) => {
          if (!raw) return null
          const k = String(raw).trim().toLowerCase()
          if (scMap[k]) return scMap[k]
          const stripped = k.replace(/[\s\.\-]/g, '')
          if (scMap[stripped]) return scMap[stripped]
          const found = Object.keys(scMap).find(key => key === k || key.replace(/[\s\.\-]/g,'') === stripped)
          return found ? scMap[found] : null
        }
        const poMap = {}; pos.forEach(p => { poMap[String(p.po_number).trim().toLowerCase()] = p.id })
        let levelMismatch = 0
        let noCostCodeCount = 0
        const skippedRows = []
        const normalized = rows.map((row, idx) => {
          const l = {}
          Object.keys(row).forEach(k => { l[k.toLowerCase().replace(/[\s\/\.\-]+/g, '_')] = row[k] })
          const qty = parseFloat(l.qty_issued || l.qty || 0) || 0
          const rate = parseFloat(l.unit_rate || l.rate || 0) || 0
          const poNum = String(l.po_number || '').trim().toLowerCase()
          const poId = poNum ? (poMap[poNum] || null) : null
          const rawCode = String(l.sub_code || l.cost_code || '').trim()
          const sc = resolveSubCode(rawCode)
          const material = String(l.material || l.description || '').trim()
          if (!rawCode) {
            // F-06/Cost Code warning: distinct from levelMismatch below -- this row
            // has no code at all, not a code that failed to resolve to a valid Sub Code.
            noCostCodeCount++
          } else if (!sc) {
            const anyMatch = costCodes.find(c=>c.code.trim().toLowerCase()===rawCode.toLowerCase())
            if (anyMatch) levelMismatch++
          }
          // F-15: record why a row would be skipped, instead of silently dropping it
          // via .filter() with no trace. Row numbers are 1-based + header row offset
          // to match what the user sees when they open the spreadsheet.
          if (!material) skippedRows.push({ row: idx+2, reason: 'Missing Material/Description' })
          else if (!(qty > 0)) skippedRows.push({ row: idx+2, reason: `Invalid or missing Qty (${l.qty_issued || l.qty || '—'})` })
          return {
            project_id: selectedProject.id,
            po_id: poId,
            ref_code: String(l.ref_code || '').trim() || null,
            inventory_ref: String(l.ref_code || '').trim() || null,
            material,
            sub_code: sc?.code || rawCode || null,
            cost_code_id: sc?.costCodeId || null,
            unit: String(l.unit || '').trim() || null,
            qty_issued: qty,
            unit_rate: rate,
            issued_date: l.issued_date || l.date || null,
            period_month: l.period_month ? String(l.period_month).trim().slice(0,7)+'-01' : new Date().toISOString().slice(0,7)+'-01',
            issued_by: String(l.issued_by || '').trim() || null,
          }
        }).filter(r => r.material && r.qty_issued > 0)
        if (levelMismatch > 0) showToast(`⚠ ${levelMismatch} صف فيه كود مش Sub Code فعلي في الشجرة`, 'error')
        resolve({ rows: normalized, skippedRows, noCostCodeCount, levelMismatch })
      } catch (err) { reject(err) }
    }
    reader.onerror = reject; reader.readAsArrayBuffer(file)
  })

  const handleIssuesFileDrop = async (file) => {
    if (!file || !selectedProject) return
    const ext = file.name.split('.').pop().toLowerCase()
    if (!['xlsx', 'xls', 'csv'].includes(ext)) return showToast('Excel or CSV only', 'error')
    try {
      const { rows, skippedRows, noCostCodeCount } = await parseIssuesExcel(file)
      if (!rows.length) return showToast('No valid rows', 'error')
      const likelyDuplicates = findLikelyDuplicates(rows, issues, 'issue')
      setIssuesUploadPreview(rows)
      setIssuesSkippedRows(skippedRows)
      setIssuesNoCostCodeCount(noCostCodeCount)
      setIssuesLikelyDuplicates(likelyDuplicates)
      setIssuesImportConfirmed(false)
    } catch (e) { showToast('Parse error: ' + e.message, 'error') }
  }

  const handleIssuesUploadConfirm = async () => {
    setIssuesUploading(true)
    // insert in batches of 200 to avoid payload limits
    const BATCH = 200
    let totalInserted = 0
    let lastError = null
    for (let i = 0; i < issuesUploadPreview.length; i += BATCH) {
      const batch = issuesUploadPreview.slice(i, i + BATCH).map(r => {
        // Keep cost_code_id as an explicit null rather than deleting the key.
        // Previously `delete row.cost_code_id` when falsy caused rows within
        // the same batch to have inconsistent object shapes once rows without
        // a Cost Code became importable (Cost Code Warning feature) -- some
        // rows had the key, some didn't, which broke Supabase's batch insert
        // ("column does not exist"). null is a normal SQL value; every row
        // in the batch must have the same set of keys.
        return { ...r, cost_code_id: r.cost_code_id || null }
      })
      const { error } = await supabase.from('store_issues').insert(batch)
      if (error) { lastError = error; break }
      totalInserted += batch.length
    }
    setIssuesUploading(false)
    if (lastError) showToast('Upload failed: ' + lastError.message, 'error')
    else { showToast(`${totalInserted} issues imported ✓`); setIssuesUploadPreview(null); loadData(selectedProject.id) }
  }

  const handleDelete = async (table, ids) => {
    if(!window.confirm(`Delete ${ids.size} record(s)?`)) return
    const arr = [...ids]
    const BATCH = 100
    let lastError = null
    for (let i = 0; i < arr.length; i += BATCH) {
      const { error } = await supabase.from(table).delete().in('id', arr.slice(i, i + BATCH))
      if (error) { lastError = error; break }
    }
    if(lastError) showToast('Delete failed: '+lastError.message,'error')
    else { showToast(`${arr.length} records deleted`); setSelected(new Set()); loadData(selectedProject.id) }
  }

  const exportStorePDF = () => {
    const today = new Date().toLocaleDateString('en-GB')
    const projectName = selectedProject?.name || 'Project'
    // Use filtered inventory to compute accurate totals for the report.
    // Stock Value here reads item.stockValue directly from the shared SSOT
    // (inventory array) — Weighted Avg. Cost method, same formula used by
    // the KPI card, Inventory table, and Excel report. No separate
    // Last-Purchase-Rate calculation exists in this function anymore.
    const filteredRefs = new Set(filteredInventory.map(i=>(i.ref_code||i.material||'').trim().toUpperCase()))
    const totalRec = receipts.filter(r=>filteredRefs.has((r.ref_code||r.material||'').trim().toUpperCase())).reduce((s,r)=>s+(parseFloat(r.total_exc_vat)||0),0)
    const totalIss = issues.filter(i=>filteredRefs.has((i.ref_code||i.inventory_ref||i.material||'').trim().toUpperCase())).reduce((s,i)=>s+(parseFloat(i.amount)||0),0)
    const stockVal = filteredInventory.reduce((s,i)=>s+i.stockValue,0)
    const negItems = filteredInventory.filter(i=>i.balance<0).length
    const sorted = [...filteredInventory].sort((a,b)=> b.stockValue - a.stockValue)
    const rows = sorted.map((item,idx)=>{
      // Fix (2026-08-12, acceptance test finding): normalize -0 to 0 before
      // display. balance × 0 rate produces JS negative-zero, which
      // toLocaleString renders literally as "-0.00" -- mathematically correct
      // but visually misleading in a report. Also show "—" for Wtd. Avg. Rate
      // when there were zero Receipts (no price to average), matching the
      // Inventory screen's existing convention instead of a bare "0.00".
      const sv = item.stockValue === 0 ? 0 : item.stockValue
      const balColor = item.balance<0?'#C0392B':item.balance>0?'#1A7A4A':'#374151'
      const svColor = sv<0?'#C0392B':sv>0?'#1A7A4A':'#374151'
      const statusColors = { negative:'#C0392B', out:'#C0392B', critical:'#C0392B', low:'#B7770D', in_stock:'#1A7A4A' }
      const statusColor = statusColors[item.stockStatus] || '#374151'
      const wtdAvgDisplay = item.received > 0
        ? Number(item.weightedAvgRate).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})
        : '—'
      return `<tr style="background:${idx%2===0?'#f8fafc':'#fff'}">
        <td style="padding:5px 8px;text-align:center;color:#64748b">${idx+1}</td>
        <td style="padding:5px 8px;font-family:monospace;color:#1B3A6B;font-size:11px">${item.ref_code||'—'}</td>
        <td style="padding:5px 8px">${item.material||'—'}</td>
        <td style="padding:5px 8px;text-align:center">${item.unit||'—'}</td>
        <td style="padding:5px 8px;text-align:right">${Number(item.pureReceived).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td style="padding:5px 8px;text-align:right">${Number(item.issued).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td style="padding:5px 8px;text-align:right;color:#1A7A4A">${item.transferIn>0?Number(item.transferIn).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}):'—'}</td>
        <td style="padding:5px 8px;text-align:right;color:#B7770D">${item.transferOut>0?Number(item.transferOut).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}):'—'}</td>
        <td style="padding:5px 8px;text-align:right;font-weight:700;color:${balColor}">${Number(item.balance).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td style="padding:5px 8px;text-align:right">${wtdAvgDisplay}</td>
        <td style="padding:5px 8px;text-align:right;font-weight:700;color:${svColor}">${Number(sv).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td style="padding:5px 8px;text-align:center;font-weight:700;color:${statusColor}">${item.stockStatusLabel||'—'}</td>
      </tr>`
    }).join('')
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
    <title>Store Inventory Report — ${projectName}</title>
    <style>
      @page { size: A4 landscape; margin: 12mm; }
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:Arial,sans-serif;font-size:12px;color:#1e293b}
      .header{background:#0F2744;color:white;padding:12px 18px;border-radius:8px;margin-bottom:14px;display:flex;justify-content:space-between;align-items:center}
      .header h1{font-size:17px;font-weight:700}
      .header p{font-size:10px;opacity:0.9;margin-top:2px}
      .cards{display:flex;gap:8px;margin-bottom:14px}
      .card{flex:1;border-radius:7px;padding:9px 12px;color:white}
      .card label{font-size:9px;opacity:0.85;display:block}
      .card span{font-size:13px;font-weight:700;display:block;margin-top:2px}
      .valuation-note{font-size:9px;color:#64748b;margin-bottom:10px;font-style:italic}
      table{width:100%;border-collapse:collapse;font-size:10px}
      thead th{background:#0F2744;color:white;padding:6px 7px;text-align:left;font-weight:600;white-space:nowrap}
      thead th.r{text-align:right}
      tfoot td{background:#f1f5f9;font-weight:700;padding:6px 7px;border-top:2px solid #cbd5e1}
      tfoot td.r{text-align:right}
      .footer{margin-top:12px;text-align:center;font-size:9px;color:#94a3b8}
      @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style></head><body>
    <div class="header">
      <div><h1>Store Inventory Report</h1><p>${projectName}</p></div>
      <div style="text-align:right"><p style="font-size:12px;font-weight:700">${today}</p><p>CostSpace Pro</p></div>
    </div>
    <div class="valuation-note">Stock Value is calculated using Weighted Average Cost per material (Total Receipt Value ÷ Total Received Qty × current Balance) — not the last purchase rate.</div>
    <div class="cards">
      <div class="card" style="background:#1B3A6B"><label>Total Received (Exc.VAT)</label><span>${Number(totalRec).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</span></div>
      <div class="card" style="background:#1A7A4A"><label>Total Issued = Actual Cost</label><span>${Number(totalIss).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</span></div>
      <div class="card" style="background:${stockVal>=0?'#B7770D':'#C0392B'}"><label>Stock Value (Weighted Avg. Cost)</label><span>${Number(Math.abs(stockVal)).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</span></div>
      <div class="card" style="background:#64748b"><label>Total Materials</label><span>${filteredInventory.length}</span></div>
      <div class="card" style="background:${negItems>0?'#C0392B':'#1A7A4A'}"><label>Negative Balance Items</label><span>${negItems}</span></div>
    </div>
    <table>
      <thead><tr>
        <th style="width:28px;text-align:center">#</th>
        <th>Ref Code</th><th>Material Description</th>
        <th style="text-align:center">Unit</th>
        <th class="r">Qty Received</th><th class="r">Qty Issued</th>
        <th class="r">Transfer In</th><th class="r">Transfer Out</th>
        <th class="r">Balance</th><th class="r">Wtd. Avg. Rate</th><th class="r">Stock Value</th>
        <th style="text-align:center">Status</th>
      </tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr>
        <td colspan="4" class="r" style="padding:6px 7px">TOTAL</td>
        <td class="r">${Number(filteredInventory.reduce((s,i)=>s+i.pureReceived,0)).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td class="r">${Number(filteredInventory.reduce((s,i)=>s+i.issued,0)).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td class="r">${Number(filteredInventory.reduce((s,i)=>s+i.transferIn,0)).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td class="r">${Number(filteredInventory.reduce((s,i)=>s+i.transferOut,0)).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td class="r">${Number(filteredInventory.reduce((s,i)=>s+i.balance,0)).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td></td>
        <td class="r">${Number(stockVal).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td></td>
      </tr></tfoot>
    </table>
    <div class="footer">CostSpace Pro — Store Inventory Report | ${projectName} | Generated: ${today}</div>
    <script>window.onload=()=>{window.print()}</script>
    </body></html>`
    const win = window.open('', '_blank')
    win.document.write(html)
    win.document.close()
  }

    const exportInventoryReport = () => {
    const rows = []
    inventory.forEach(item => {
      // Header row for each material
      rows.push({
        'Ref Code': item.ref_code || '—',
        'Material Description': item.material,
        'Unit': item.unit || '—',
        'Type': '',
        'Date': '',
        'PO Number': '',
        'Sub Code': '',
        'Qty': '',
        'Balance': '',
        'Wtd. Avg. Unit Cost': '',
        'Stock Value (Wtd. Avg.)': '',
        'Status': '',
      })
      let balance = 0
      // Merge receipts, issues, AND transfers sorted by date. Fix (2026-08-13):
      // previously this only included receipts/issues -- a material touched by
      // an approved transfer would show a correct final Closing Balance (from
      // item.balance, the shared SSOT) but an INCOMPLETE movement history
      // leading up to it, missing the Transfer Out/In rows entirely. Now uses
      // the same transferOutLines/transferInLines already loaded for the
      // shared inventorySSOT.js calculation -- no separate data fetch.
      const itemKey = (item.ref_code||item.material||'').trim().toUpperCase()
      const recs = receipts
        .filter(r => (r.ref_code||r.material||'').trim().toUpperCase() === itemKey)
        .map(r => ({ type:'Receipt', date: r.received_date||'', po: r.purchase_orders?.po_number||'—', sub_code:'—', qty: parseFloat(r.qty_received)||0 }))
      const iss = issues
        .filter(i => (i.ref_code||i.material||'').trim().toUpperCase() === itemKey)
        .map(i => ({ type:'Issue', date: i.issued_date||'', po:'—', sub_code: i.sub_code||'—', qty: -(parseFloat(i.qty_issued)||0) }))
      const transOut = transferOutLines
        .filter(l => (l.ref_code||'').trim().toUpperCase() === itemKey)
        .map(l => ({ type:'Transfer Out', date: l.inventory_transfers?.transfer_date||'', po: l.inventory_transfers?.transfer_no||'—', sub_code:'—', qty: -(parseFloat(l.qty)||0) }))
      const transIn = transferInLines
        .filter(l => (l.ref_code||'').trim().toUpperCase() === itemKey)
        .map(l => ({ type:'Transfer In', date: l.inventory_transfers?.transfer_date||'', po: l.inventory_transfers?.transfer_no||'—', sub_code:'—', qty: parseFloat(l.qty)||0 }))
      const movements = [...recs, ...iss, ...transOut, ...transIn].sort((a,b) => a.date.localeCompare(b.date))
      // F-14 (2026-08-12): explicit Opening Balance row. This report includes
      // every historical receipt/issue for the material with no date cutoff,
      // so the true opening point is always 0 -- but that was previously left
      // implicit (the report simply started printing movements with no stated
      // starting balance). Making it explicit lets the reader verify
      // Opening + Receipts - Issues = Closing directly from the sheet.
      rows.push({
        'Ref Code': '', 'Material Description': '', 'Unit': '',
        'Type': 'OPENING BALANCE', 'Date': '', 'PO Number': '', 'Sub Code': '',
        'Qty': '', 'Balance': 0,
        'Wtd. Avg. Unit Cost': '', 'Stock Value (Wtd. Avg.)': '', 'Status': '',
      })
      movements.forEach(m => {
        balance += m.qty
        rows.push({
          'Ref Code': '',
          'Material Description': '',
          'Unit': '',
          'Type': m.type,
          'Date': m.date,
          'PO Number': m.po,
          'Sub Code': m.sub_code,
          'Qty': Math.abs(m.qty),
          'Balance': balance,
          'Wtd. Avg. Unit Cost': '',
          'Stock Value (Wtd. Avg.)': '',
          'Status': '',
        })
      })
      // Summary row — Closing Balance here. Identity: Opening(0) + sum(Receipts)
      // - sum(Issues) = Closing (= item.balance from the shared SSOT). Stock
      // Value uses Weighted Average Cost (Total Receipt Value ÷ Total Received
      // Qty × Balance), same method as the KPI card, Inventory table, and PDF
      // report. Status uses the same unified getMaterialStockStatus() result.
      rows.push({
        'Ref Code': '', 'Material Description': '', 'Unit': '',
        'Type': 'CLOSING BALANCE', 'Date': '', 'PO Number': '', 'Sub Code': '',
        'Qty': '', 'Balance': item.balance,
        'Wtd. Avg. Unit Cost': item.weightedAvgRate,
        'Stock Value (Wtd. Avg.)': item.stockValue,
        'Status': item.stockStatusLabel || '',
      })
      rows.push({}) // empty separator
    })
    const ws = XLSX.utils.json_to_sheet(rows)
    ws['!cols'] = [20,40,8,10,12,14,14,10,10,16,18,14].map(w=>({wch:w}))
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory Movement')
    XLSX.writeFile(wb, `store_movement_${selectedProject?.name||'report'}.xlsx`)
  }

  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      { material:'Reinforcement Steel 12mm', ref_code:'STL-12', unit:'TON', qty_received:10, unit_rate:3500, total_exc_vat:35000, total_inc_vat:40250, received_date:'2025-01-15', period_month:'2025-01-01', received_by:'Ahmed Ali', po_number:'PO-2025-001', sub_code:'S-C.Dew.1' },
    ])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Receipts')
    XLSX.writeFile(wb, 'store_receipts_template.xlsx')
  }

  const tabs = [
    { id:'po', label:'🛒 Receive/Issue by PO' },
    { id:'receipts', label:'📦 All Receipts', count: receipts.length },
    { id:'issues', label:'🔧 All Issues', count: issues.length },
    { id:'inventory', label:'🏪 Inventory', count: inventory.length },
    { id:'suppliers', label:'🏢 Suppliers', count: supplierAnalysis.length },
  ]

  return (
    <div style={{minHeight:"100vh",background:"#F3F4F6",fontFamily:"inherit"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #E5E7EB",padding:"18px 24px 14px"}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:16}}>
          <div>
            <h1 className="text-xl font-extrabold text-[#0F1017] tracking-tight">Store Management</h1>
            <p className="text-xs text-[#6B7280] mt-0.5">Receipts · Issues · Inventory — Material Actual Cost</p>
          </div>
          {selectedProject && activeTab==='receipts' && (
            <div className="flex gap-2">
              <button onClick={downloadTemplate} className="flex items-center gap-1.5 px-3 py-2 text-xs text-[#6B7280] border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA]"><DownloadIcon/> Template</button>
              {can('Store.Create') && (
                <label className="flex items-center gap-1.5 px-3 py-2 text-xs text-[#6B7280] border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA] cursor-pointer">
                  <UploadIcon/> Upload
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={e=>handleFileDrop(e.target.files[0])}/>
                </label>
              )}
              {can('Store.Create') && (
                <button onClick={()=>setModal({type:'receipt',poLine:null,po:null})}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-[#1A7A4A] rounded-lg hover:bg-[#15693f]">
                  <PlusIcon/> New Receipt
                </button>
              )}
            </div>
          )}
          {selectedProject && activeTab==='issues' && (
            <div className="flex gap-2">
              <button onClick={downloadIssuesTemplate} className="flex items-center gap-1.5 px-3 py-2 text-xs text-[#6B7280] border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA]"><DownloadIcon/> Template</button>
              {can('Store.Create') && (
                <label className="flex items-center gap-1.5 px-3 py-2 text-xs text-[#6B7280] border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA] cursor-pointer">
                  <UploadIcon/> Bulk Upload
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={e=>handleIssuesFileDrop(e.target.files[0])}/>
                </label>
              )}
              {can('Store.Create') && (
                <button onClick={()=>setModal({type:'issue',poLine:null,po:null})}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-[#1A7A4A] rounded-lg hover:bg-[#15693F]">
                  <PlusIcon/> New Issue
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{padding:"20px 24px",display:"flex",flexDirection:"column",gap:16}}>
        {/* Project Selector */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-3">Select Project</label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {projects.map(p=>(
              <button key={p.id} onClick={()=>handleProjectSelect(p)}
                className={`text-left px-4 py-3 rounded-xl border transition-all ${selectedProject?.id===p.id?'border-[#1A7A4A] bg-[#EDE7DA] ring-2 ring-[#1A7A4A]/30':'border-[#E5E7EB] hover:border-[#1A7A4A]/50'}`}>
                <p className="text-sm font-semibold text-[#1C1A18]">{p.name}</p>
                <p className="text-xs text-[#9CA3AF]">{p.organizations?.name}</p>
              </button>
            ))}
          </div>
        </div>

        {selectedProject && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <div className="text-lg font-bold text-[#1A7A4A]">{fmtM(totalReceived)}</div>
                <div className="text-xs text-[#6B7280] mt-1">Total Received (Exc.VAT)</div>
              </div>
              <div className="bg-[#1A7A4A] rounded-xl p-4">
                <div className="text-lg font-bold text-white">{fmtM(totalIssued)}</div>
                <div className="text-xs text-emerald-200 mt-1">Total Issued = Actual Cost</div>
              </div>
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <div className="flex items-center gap-1.5">
                  <div className="text-lg font-bold text-amber-600">{fmtM(totalStockValue)}</div>
                  <span className="group relative inline-flex items-center cursor-help">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5 text-[#9CA3AF]"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-64 p-2.5 bg-[#1C1A18] text-white text-xs rounded-lg shadow-xl z-50 pointer-events-none">
                      Valued using Weighted Average Cost per material (Total Receipt Value ÷ Total Received Qty), applied to current Balance. Not Last Purchase Rate, not Received−Issued value.
                    </span>
                  </span>
                </div>
                <div className="text-xs text-[#6B7280] mt-1">Stock Value (Weighted Avg. Cost)</div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-[#EDE7DA] rounded-xl border border-[#E5E7EB] p-1">
              {tabs.map(t=>(
                <button key={t.id} onClick={()=>{setActiveTab(t.id);setSearch('');setSelected(new Set());setUploadPreview(null);setSelectedPO(null);setIssueSubCode('all')}}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-colors ${activeTab===t.id?'bg-[#1C1A18] text-white font-medium':'text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                  {t.label}
                  {t.count!==undefined && <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab===t.id?'bg-amber-500 text-white':'bg-[#EDE7DA] text-[#6B7280]'}`}>{t.count}</span>}
                </button>
              ))}
            </div>

            {/* ── TAB: Receive/Issue by PO ── */}
            {activeTab === 'po' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* PO List */}
                <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#F3F4F6]">
                    <p className="text-xs font-semibold text-[#6B7280] uppercase mb-2">Material POs</p>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none"><SearchIcon/></span>
                      <input value={poSearch} onChange={e=>setPOSearch(e.target.value)} placeholder="Search PO number..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
                    </div>
                  </div>
                  <div className="divide-y divide-[#F3F4F6] max-h-96 overflow-y-auto">
                    {filteredPOs.length===0 ? (
                      <p className="px-4 py-6 text-xs text-[#9CA3AF] text-center">No material POs found</p>
                    ) : filteredPOs.map(po=>(
                      <button key={po.id} onClick={()=>handlePOSelect(po)}
                        className={`w-full text-left px-4 py-3 hover:bg-[#FAFAFA] transition-colors ${selectedPO?.id===po.id?'bg-[#EDE7DA] border-r-2 border-[#1A7A4A]':''}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-[#1A7A4A]">{po.po_number}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${po.status==='approved'||po.status==='active'?'bg-emerald-100 text-emerald-700 border-emerald-200':'bg-[#EDE7DA] text-[#6B7280] border-[#E5E7EB]'}`}>{po.status}</span>
                        </div>
                        <p className="text-xs text-[#6B7280] mt-0.5">{po.vendor}</p>
                        <p className="text-xs text-[#1A7A4A] mt-0.5">{fmtM(po.total_exc_vat)} Exc.VAT</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* PO Line Items */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                  {!selectedPO ? (
                    <div className="flex flex-col items-center justify-center h-64 text-[#9CA3AF]">
                      <p className="text-sm">← Select a PO to view line items</p>
                    </div>
                  ) : (
                    <>
                      <div className="px-5 py-4 border-b border-[#F3F4F6] bg-[#F5F1EA]">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-[#1C1A18]">{selectedPO.po_number} — {selectedPO.vendor}</p>
                            <p className="text-xs text-[#6B7280]">{selectedPO.issue_date} · {selectedPO.po_type} · {selectedPO.cost_type}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {can('Store.Create') && (
                              <button onClick={()=>setModal({type:'bulk',bulkType:'receipt',po:selectedPO})}
                                className="px-3 py-1.5 text-xs bg-[#DDD9D0] text-[#1A7A4A] rounded-lg hover:bg-indigo-200 font-medium whitespace-nowrap">
                                ⬇ Bulk Receive
                              </button>
                            )}
                            {can('Store.Create') && (
                              <button onClick={()=>setModal({type:'bulk',bulkType:'issue',po:selectedPO})}
                                className="px-3 py-1.5 text-xs bg-[#1A7A4A] text-white rounded-lg hover:bg-[#15693F] font-medium whitespace-nowrap">
                                ⬆ Bulk Issue
                              </button>
                            )}
                            <p className="text-sm font-bold text-[#1A7A4A] ml-2">{fmtM(selectedPO.total_exc_vat)} Exc.VAT</p>
                          </div>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                            <tr>
                              {['Ref Code','Description','Unit','PO Qty','Received','Issued','Balance',''].map(h=>(
                                <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-[#6B7280] uppercase whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#F3F4F6]">
                            {poLines.map(line => {
                              const stats = getPOLineStats(line.id, selectedPO?.id, line.ref_code)
                              const balance = stats.received - stats.issued
                              const lineStatus = getMaterialStockStatus({ balance, received: stats.received, ref_code: line.ref_code })
                              return (
                                <tr key={line.id} className="hover:bg-[#FAFAFA]">
                                  <td className="px-3 py-3 font-mono text-xs text-[#1A7A4A]">{line.ref_code||'—'}</td>
                                  <td className="px-3 py-3 text-[#2E2C29] max-w-xs">
                                    <p>{line.description}</p>
                                    {line.cost_codes && <p className="text-xs text-[#9CA3AF] mt-0.5">{line.cost_codes.code}</p>}
                                  </td>
                                  <td className="px-3 py-3 text-xs text-[#6B7280]">{line.unit||'—'}</td>
                                  <td className="px-3 py-3 text-right text-xs font-medium">{fmt(line.qty)}</td>
                                  <td className="px-3 py-3 text-right text-xs text-[#1A7A4A] font-medium">{fmt(stats.received)}</td>
                                  <td className="px-3 py-3 text-right text-xs text-[#1A7A4A] font-medium">{fmt(stats.issued)}</td>
                                  <td className="px-3 py-3 text-right text-xs">
                                    <span className={`font-medium ${lineStatus.status==='negative'?'text-red-500':lineStatus.status==='out'?'text-red-400':lineStatus.status==='critical'||lineStatus.status==='low'?'text-amber-500':'text-[#6B7280]'}`}>
                                      {balance===0?'—':fmt(balance)}
                                    </span>
                                    {lineStatus.status==='negative' && <span className="block text-xs text-red-400">Negative</span>}
                                    {lineStatus.status==='out' && <span className="block text-xs text-red-400">Out of stock</span>}
                                    {lineStatus.status==='critical' && <span className="block text-xs text-amber-500">Critical</span>}
                                    {lineStatus.status==='low' && <span className="block text-xs text-amber-500">Low Stock</span>}
                                  </td>
                                  <td className="px-3 py-3">
                                    <div className="flex gap-1">
                                      {can('Store.Create') && (
                                        <button
                                          onClick={()=>setModal({type:'receipt',poLine:line,po:selectedPO})}
                                          className="px-2 py-1 text-xs bg-[#DDD9D0] text-[#1A7A4A] rounded-lg hover:bg-indigo-200 font-medium whitespace-nowrap">
                                          + Receive
                                        </button>
                                      )}
                                      {can('Store.Create') && (
                                        <button
                                          onClick={()=>setModal({type:'issue',poLine:line,po:selectedPO})}
                                          className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 font-medium whitespace-nowrap">
                                          + Issue
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Upload Preview */}
            {activeTab==='receipts' && uploadPreview && (
              <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                {(receiptsLikelyDuplicates.length>0 || receiptsSkippedRows.length>0) && (
                  <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 space-y-2">
                    {receiptsLikelyDuplicates.length>0 && (
                      <div className="text-xs text-amber-800">
                        <p>⚠ {receiptsLikelyDuplicates.length} صف يشبه معاملة موجودة بالفعل (نفس Ref Code + Qty + Date):</p>
                        <ul className="ml-4 mt-1 space-y-0.5">
                          {receiptsLikelyDuplicates.slice(0,5).map((d,i)=>(
                            <li key={i}>Row {d.row}: {d.ref_code} — Qty {d.qty} — {d.date||'no date'}</li>
                          ))}
                          {receiptsLikelyDuplicates.length>5 && <li>... و {receiptsLikelyDuplicates.length-5} صف تاني</li>}
                        </ul>
                      </div>
                    )}
                    {receiptsSkippedRows.length>0 && (
                      <div className="text-xs text-red-700">
                        <p>✗ {receiptsSkippedRows.length} صف اتجاهل من الملف (مش هيتسجل):</p>
                        <ul className="ml-4 mt-1 space-y-0.5">
                          {receiptsSkippedRows.slice(0,5).map((s,i)=>(
                            <li key={i}>Row {s.row}: {s.reason}</li>
                          ))}
                          {receiptsSkippedRows.length>5 && <li>... و {receiptsSkippedRows.length-5} صف تاني</li>}
                        </ul>
                      </div>
                    )}
                    <label className="flex items-center gap-1.5 text-xs text-amber-900 font-medium pt-1">
                      <input type="checkbox" checked={receiptsImportConfirmed} onChange={e=>setReceiptsImportConfirmed(e.target.checked)}/>
                      فهمت التحذيرات دي وعايز أكمل الاستيراد
                    </label>
                  </div>
                )}
                <div className="flex items-center justify-between px-5 py-4 bg-amber-50 border-b border-[#F3F4F6]">
                  <p className="text-sm font-semibold text-amber-800">Preview — {uploadPreview.length} receipts</p>
                  <div className="flex gap-2">
                    <button onClick={()=>setUploadPreview(null)} className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-lg text-[#6B7280] hover:bg-[#FAFAFA]">Cancel</button>
                    <button onClick={handleUploadConfirm}
                      disabled={uploading || ((receiptsLikelyDuplicates.length>0 || receiptsSkippedRows.length>0) && !receiptsImportConfirmed)}
                      className="px-4 py-1.5 text-xs font-medium text-white bg-[#1A7A4A] rounded-lg hover:bg-[#15693f] disabled:opacity-50">
                      {uploading?'Importing...':`Import ${uploadPreview.length}`}
                    </button>
                  </div>
                </div>
                <div className="overflow-auto max-h-48">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F5F1EA]"><tr>{['Material','Ref Code','Unit','Qty','Rate','Total Exc.VAT','GRN','PO'].map(h=><th key={h} className="text-left px-3 py-2 font-medium text-[#6B7280]">{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {uploadPreview.slice(0,10).map((r,i)=>(
                        <tr key={i}>
                          <td className="px-3 py-2">{r.material}</td>
                          <td className="px-3 py-2 font-mono text-[#1A7A4A]">{r.ref_code||'—'}</td>
                          <td className="px-3 py-2">{r.unit||'—'}</td>
                          <td className="px-3 py-2 text-right">{fmt(r.qty_received)}</td>
                          <td className="px-3 py-2 text-right">{fmt(r.unit_rate)}</td>
                          <td className="px-3 py-2 text-right font-medium text-[#1A7A4A]">{fmt(r.total_exc_vat)}</td>
                          <td className="px-3 py-2">{r.received_by||'—'}</td>
                          <td className="px-3 py-2 text-[#1A7A4A]">{r.po_id?'✓ Linked':'—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Receipts Tab */}
            {activeTab==='receipts' && (
              <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[#F3F4F6]">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none"><SearchIcon/></span>
                    <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search material, ref code, PO number..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
                  </div>
                  {selected.size>0 && can('Store.Delete') && <button onClick={()=>handleDelete('store_receipts',selected)} className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"><TrashIcon/> Delete ({selected.size})</button>}
                  <span className="text-xs text-[#9CA3AF]">{filteredReceipts.length} records</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                      <tr>
                        <th className="w-10 px-4 py-3"><input type="checkbox" checked={selected.size===filteredReceipts.length&&filteredReceipts.length>0} onChange={()=>selected.size===filteredReceipts.length?setSelected(new Set()):setSelected(new Set(filteredReceipts.map(r=>r.id)))} className="w-4 h-4 text-[#1A7A4A] rounded"/></th>
                        {['PO Number','Ref Code','Material Description','Unit','Qty Received','Rate','Date','Delivery Note','Received By'].map(h=><th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase whitespace-nowrap">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {filteredReceipts.length===0?(
                        <tr><td colSpan={10} className="px-4 py-12 text-center text-[#9CA3AF] text-sm">No receipts yet</td></tr>
                      ):filteredReceipts.map(r=>(
                        <tr key={r.id} className={`hover:bg-[#FAFAFA] ${selected.has(r.id)?'bg-[#EDE7DA]/50':''}`}>
                          <td className="px-4 py-3"><input type="checkbox" checked={selected.has(r.id)} onChange={()=>{const s=new Set(selected);s.has(r.id)?s.delete(r.id):s.add(r.id);setSelected(s)}} className="w-4 h-4 text-[#1A7A4A] rounded"/></td>
                          <td className="px-4 py-3 font-mono text-xs text-[#1A7A4A]">{r.purchase_orders?.po_number||'—'}</td>
                          <td className="px-4 py-3 font-mono text-xs text-[#6B7280]">{r.ref_code||'—'}</td>
                          <td className="px-4 py-3 text-[#2E2C29] max-w-xs truncate">{r.material}</td>
                          <td className="px-4 py-3 text-xs">{r.unit||'—'}</td>
                          <td className="px-4 py-3 text-right font-semibold text-[#1A7A4A]">{fmt(r.qty_received)}</td>
                          <td className="px-4 py-3 text-right text-xs">{fmt(r.unit_rate)}</td>
                          <td className="px-4 py-3 text-xs text-[#6B7280]">{r.received_date||'—'}</td>
                          <td className="px-4 py-3 text-xs text-[#6B7280]">{r.delivery_note_no||'—'}</td>
                          <td className="px-4 py-3 text-xs text-[#6B7280]">{r.received_by||'—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    {filteredReceipts.length>0&&<tfoot className="bg-[#F5F1EA] border-t-2 border-[#E5E7EB]"><tr>
                      <td colSpan={5} className="px-4 py-3 text-xs font-bold text-[#6B7280] uppercase">Total Qty</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-[#1A7A4A]">{fmt(filteredReceipts.reduce((s,r)=>s+(parseFloat(r.qty_received)||0),0))}</td>
                      <td colSpan={4}/>
                    </tr></tfoot>}
                  </table>
                </div>
              </div>
            )}

            {/* Issues Upload Preview */}
            {activeTab==='issues' && issuesUploadPreview && (
              <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                {(issuesNoCostCodeCount>0 || issuesLikelyDuplicates.length>0 || issuesSkippedRows.length>0) && (
                  <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 space-y-2">
                    {issuesNoCostCodeCount>0 && (
                      <p className="text-xs text-amber-800">⚠ {issuesNoCostCodeCount} صف بدون Cost Code/Sub Code — هيتسجل بدون تصنيف تكلفة.</p>
                    )}
                    {issuesLikelyDuplicates.length>0 && (
                      <div className="text-xs text-amber-800">
                        <p>⚠ {issuesLikelyDuplicates.length} صف يشبه معاملة موجودة بالفعل (نفس Ref Code + Qty + Date):</p>
                        <ul className="ml-4 mt-1 space-y-0.5">
                          {issuesLikelyDuplicates.slice(0,5).map((d,i)=>(
                            <li key={i}>Row {d.row}: {d.ref_code} — Qty {d.qty} — {d.date||'no date'}</li>
                          ))}
                          {issuesLikelyDuplicates.length>5 && <li>... و {issuesLikelyDuplicates.length-5} صف تاني</li>}
                        </ul>
                      </div>
                    )}
                    {issuesSkippedRows.length>0 && (
                      <div className="text-xs text-red-700">
                        <p>✗ {issuesSkippedRows.length} صف اتجاهل من الملف (مش هيتسجل):</p>
                        <ul className="ml-4 mt-1 space-y-0.5">
                          {issuesSkippedRows.slice(0,5).map((s,i)=>(
                            <li key={i}>Row {s.row}: {s.reason}</li>
                          ))}
                          {issuesSkippedRows.length>5 && <li>... و {issuesSkippedRows.length-5} صف تاني</li>}
                        </ul>
                      </div>
                    )}
                    <label className="flex items-center gap-1.5 text-xs text-amber-900 font-medium pt-1">
                      <input type="checkbox" checked={issuesImportConfirmed} onChange={e=>setIssuesImportConfirmed(e.target.checked)}/>
                      فهمت التحذيرات دي وعايز أكمل الاستيراد
                    </label>
                  </div>
                )}
                <div className="flex items-center justify-between px-5 py-4 bg-emerald-50 border-b border-[#F3F4F6]">
                  <p className="text-sm font-semibold text-emerald-800">Preview — {issuesUploadPreview.length} issues</p>
                  <div className="flex gap-2">
                    <button onClick={()=>setIssuesUploadPreview(null)} className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-lg text-[#6B7280] hover:bg-[#FAFAFA]">Cancel</button>
                    <button onClick={handleIssuesUploadConfirm}
                      disabled={issuesUploading || ((issuesNoCostCodeCount>0 || issuesLikelyDuplicates.length>0 || issuesSkippedRows.length>0) && !issuesImportConfirmed)}
                      className="px-4 py-1.5 text-xs font-medium text-white bg-[#1A7A4A] rounded-lg hover:bg-[#15693F] disabled:opacity-50">
                      {issuesUploading?'Importing...':`Import ${issuesUploadPreview.length}`}
                    </button>
                  </div>
                </div>
                <div className="overflow-auto max-h-48">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F5F1EA]">
                      <tr>{['PO','Ref Code','Material','Cost Code','Unit','Qty','Rate','Total Exc.VAT'].map(h=>(
                        <th key={h} className="text-left px-3 py-2 font-medium text-[#6B7280]">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {issuesUploadPreview.slice(0,10).map((r,i)=>(
                        <tr key={i}>
                          <td className="px-3 py-2 text-[#1A7A4A]">{r.po_id?'✓ Linked':'—'}</td>
                          <td className="px-3 py-2 font-mono text-[#1A7A4A]">{r.ref_code||'—'}</td>
                          <td className="px-3 py-2 max-w-xs truncate">{r.material}</td>
                          <td className="px-3 py-2 font-mono text-[#9CA3AF]">{r.cost_code_id?'✓':'—'}</td>
                          <td className="px-3 py-2">{r.unit||'—'}</td>
                          <td className="px-3 py-2 text-right">{fmt(r.qty_issued)}</td>
                          <td className="px-3 py-2 text-right">{fmt(r.unit_rate)}</td>
                          <td className="px-3 py-2 text-right font-medium text-[#1A7A4A]">{fmt(r.amount)}</td>
                        </tr>
                      ))}
                      {issuesUploadPreview.length>10 && (
                        <tr><td colSpan={8} className="px-3 py-2 text-[#9CA3AF] text-center">... و {issuesUploadPreview.length-10} بند تاني</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Issues Tab */}
            {activeTab==='issues' && (
              <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[#F3F4F6]">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none"><SearchIcon/></span>
                    <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search material, ref code, sub code..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
                  </div>
                  {selected.size>0 && can('Store.Delete') && <button onClick={()=>handleDelete('store_issues',selected)} className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"><TrashIcon/> Delete ({selected.size})</button>}
                  <span className="text-xs text-[#9CA3AF]">{filteredIssues.length} records</span>
                  <div className="flex items-center gap-2 ml-2 pl-2 border-l border-[#E5E7EB]">
                    <span className="text-xs text-[#6B7280]">Date:</span>
                    <input type="date" value={bulkDate} onChange={e=>setBulkDate(e.target.value)} className="border border-[#E5E7EB] rounded px-2 py-1 text-xs focus:outline-none focus:border-[#1A7A4A]"/>
                    <button onClick={()=>applyBulkDate(selected.size>0?[...selected]:filteredIssues.map(i=>i.id))} disabled={!bulkDate||bulkUpdating} className="px-2 py-1 bg-[#1A7A4A] text-white text-xs font-bold rounded disabled:opacity-40">
                      {bulkUpdating?'...':selected.size>0?`Apply(${selected.size})`:'Apply All'}
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                      <tr>
                        <th className="w-10 px-4 py-3"><input type="checkbox" checked={selected.size===filteredIssues.length&&filteredIssues.length>0} onChange={()=>selected.size===filteredIssues.length?setSelected(new Set()):setSelected(new Set(filteredIssues.map(i=>i.id)))} className="w-4 h-4 text-[#1A7A4A] rounded"/></th>
                        {['PO Number','Ref Code','Material','Sub Code','Cost Code','Resource Code','Resource Description','Unit','Qty Issued','Rate','Amount (Actual Cost)','Date'].map(h=><th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase whitespace-nowrap">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {filteredIssues.length===0?(
                        <tr><td colSpan={12} className="px-4 py-12 text-center text-[#9CA3AF] text-sm">No issues yet</td></tr>
                      ):filteredIssues.map(i=>{
                        // Bugfix (root-cause sub_code normalization): store_issues.sub_code
                        // is now normalized to LOWER(TRIM()) at write time by a DB trigger,
                        // consistent with the financial matching logic (get_commitment_by_subcode
                        // and the now-fixed get_actual_cost_by_subcode). This lookup previously
                        // used an exact, case-sensitive match against subCodesList (sourced from
                        // cost_codes, which keeps its original casing) — after normalization, a
                        // stored sub_code like 'arch' would no longer exact-match a cost_codes
                        // entry like 'Arch', silently blanking the Cost Code/Resource Code
                        // columns below for any newly-written row. Matching now uses the same
                        // LOWER(TRIM()) comparison on both sides, so it works for both
                        // newly-normalized rows and any pre-existing rows regardless of their
                        // stored casing.
                        const subCodeNormalized = String(i.sub_code||'').trim().toLowerCase()
                        const sub = subCodesList.find(s => String(s.code||'').trim().toLowerCase() === subCodeNormalized)
                        return (
                        <tr key={i.id} className={`hover:bg-[#FAFAFA] ${selected.has(i.id)?'bg-emerald-50/50':''}`}>
                          <td className="px-4 py-3"><input type="checkbox" checked={selected.has(i.id)} onChange={()=>{const s=new Set(selected);s.has(i.id)?s.delete(i.id):s.add(i.id);setSelected(s)}} className="w-4 h-4 text-[#1A7A4A] rounded"/></td>
                          <td className="px-4 py-3 font-mono text-xs text-[#1A7A4A]">{i.po_id ? (poMap[i.po_id]||i.po_id) : '—'}</td>
                          <td className="px-4 py-3 font-mono text-xs text-[#1A7A4A]">{i.ref_code||'—'}</td>
                          <td className="px-4 py-3 text-[#2E2C29] max-w-xs truncate">{i.material}</td>
                          <td className="px-4 py-3 text-xs font-mono text-[#1A7A4A] font-semibold">{i.sub_code||'—'}</td>
                          <td className="px-4 py-3 text-xs font-mono text-[#9CA3AF]">{sub?.costCode || i.cost_codes?.code || '—'}</td>
                          <td className="px-4 py-3 text-xs font-mono text-purple-700">{sub?.resourceCode || '—'}</td>
                          <td className="px-4 py-3 text-xs text-purple-700">{sub?.resourceCodeDescription || '—'}</td>
                          <td className="px-4 py-3 text-xs">{i.unit||'—'}</td>
                          <td className="px-4 py-3 text-right text-xs">{fmt(i.qty_issued)}</td>
                          <td className="px-4 py-3 text-right text-xs">{fmt(i.unit_rate)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-[#1A7A4A]">{fmt(i.amount)}</td>
                          <td className="px-4 py-3 text-xs text-[#6B7280]">{i.issued_date||'—'}</td>
                        </tr>
                        )
                      })}
                    </tbody>
                    {filteredIssues.length>0&&<tfoot className="bg-[#F5F1EA] border-t-2 border-[#E5E7EB]"><tr>
                      <td colSpan={9} className="px-4 py-3 text-xs font-bold text-[#6B7280] uppercase">Total</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-[#1C1A18]">{filteredIssues.reduce((s,i)=>s+(parseFloat(i.qty_issued)||0),0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                      <td/>
                      <td className="px-4 py-3 text-right text-sm font-bold text-emerald-700">{fmt(filteredIssues.reduce((s,i)=>s+(parseFloat(i.amount)||0),0))}</td>
                      <td/>
                    </tr></tfoot>}
                  </table>
                </div>
              </div>
            )}

            {/* Inventory Tab — by Ref Code (يجمع كل الـ POs) */}
            {activeTab==='inventory' && (
              <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                {/* Stock Alert Summary */}
                {(criticalItems.length > 0 || lowItems.length > 0) && (
                  <div className="flex gap-3 px-4 py-3 bg-[#F5F1EA] border-b border-[#F3F4F6]">
                    {criticalItems.length > 0 && (
                      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2">
                        <span className="text-lg">🔴</span>
                        <div>
                          <p className="text-xs font-bold text-red-700">{criticalItems.length} Critical / Negative</p>
                          <p className="text-xs text-red-500">Balance below 10% or negative</p>
                        </div>
                      </div>
                    )}
                    {lowItems.length > 0 && (
                      <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2">
                        <span className="text-lg">🟡</span>
                        <div>
                          <p className="text-xs font-bold text-amber-700">{lowItems.length} Low Stock</p>
                          <p className="text-xs text-amber-500">Balance between 10–25%</p>
                        </div>
                      </div>
                    )}
                    <button onClick={()=>setInventoryStatusFilter('critical')} className="ml-auto text-xs text-[#9CA3AF] hover:text-[#6B7280] underline">
                      Filter alerts only
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[#F3F4F6]">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none"><SearchIcon/></span>
                    <input value={inventorySearch} onChange={e=>setInventorySearch(e.target.value)} placeholder="Search material or ref code..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
                  </div>
                  <select value={inventoryStatusFilter} onChange={e=>setInventoryStatusFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A7A4A] bg-white">
                    <option value="all">All Status</option>
                    <option value="in_stock">✅ In Stock</option>
                    <option value="low">🟡 Low Stock</option>
                    <option value="critical">🔴 Critical</option>
                    <option value="out">⬜ Out of Stock</option>
                    <option value="negative">❌ Negative</option>
                  </select>
                  <div className="flex items-center gap-1">
                    <select value={inventoryBalanceFilter.op} onChange={e=>setInventoryBalanceFilter(p=>({...p,op:e.target.value}))}
                      className="px-2 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none bg-white">
                      <option value="">Balance</option>
                      <option value="lt">&lt; less than</option>
                      <option value="gt">&gt; greater than</option>
                      <option value="eq">= equal</option>
                    </select>
                    {inventoryBalanceFilter.op && (
                      <input type="number" value={inventoryBalanceFilter.val}
                        onChange={e=>setInventoryBalanceFilter(p=>({...p,val:e.target.value}))}
                        placeholder="qty" className="w-20 px-2 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]"/>
                    )}
                  </div>
                  {(inventorySearch||inventoryStatusFilter!=='all'||inventoryBalanceFilter.op) && (
                    <button onClick={()=>{setInventorySearch('');setInventoryStatusFilter('all');setInventoryBalanceFilter({op:'',val:''})}}
                      className="text-xs text-[#9CA3AF] hover:text-red-500 whitespace-nowrap">✕ Clear</button>
                  )}
                  <span className="text-xs text-[#9CA3AF] whitespace-nowrap">{filteredInventory.length} of {inventory.length}</span>
                  <button onClick={exportInventoryReport}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs text-[#6B7280] border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA] whitespace-nowrap">
                    <DownloadIcon/> Movement Report
                  </button>
                  <button onClick={exportStorePDF}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-white bg-[#1A7A4A] rounded-lg hover:bg-[#15693F] whitespace-nowrap">
                    <DownloadIcon/> PDF Report
                  </button>
                  <span className="text-xs text-[#9CA3AF]">{filteredInventory.length} materials</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                      <tr>{['Ref Code','Material','Unit','Total Received','Total Issued','Transfer In','Transfer Out','Balance','Wtd. Avg. Unit Cost','Stock Value (Wtd. Avg.)','Status'].map(h=>(
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">
                          {h === 'Stock Value (Wtd. Avg.)' ? (
                            <span className="inline-flex items-center gap-1">
                              {h}
                              <span className="group relative inline-flex items-center cursor-help">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3 h-3 text-[#9CA3AF] normal-case"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                <span className="absolute top-full left-0 mt-1 hidden group-hover:block w-60 p-2 bg-[#1C1A18] text-white text-xs normal-case font-normal rounded-lg shadow-xl z-50 pointer-events-none">
                                  Weighted Avg. Unit Cost = Total Receipt Value ÷ Total Received Qty for this material (includes approved Transfer In). Stock Value = Balance × that rate. Not the last purchase rate.
                                </span>
                              </span>
                            </span>
                          ) : h}
                        </th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {filteredInventory.length===0?(
                        <tr><td colSpan={11} className="px-4 py-12 text-center text-[#9CA3AF] text-sm">No inventory data</td></tr>
                      ):filteredInventory.map((item,i)=>(
                        <tr key={i} className="hover:bg-[#FAFAFA]">
                          <td className="px-4 py-3 font-mono text-xs font-semibold text-[#1A7A4A]">{item.ref_code||'—'}</td>
                          <td className="px-4 py-3 text-[#2E2C29]">{item.material}</td>
                          <td className="px-4 py-3 text-xs text-[#6B7280]">{item.unit||'—'}</td>
                          <td className="px-4 py-3 text-right text-[#1A7A4A] font-medium">{fmt(item.pureReceived)}</td>
                          <td className="px-4 py-3 text-right text-[#1A7A4A] font-medium">{fmt(item.issued)}</td>
                          <td className="px-4 py-3 text-right text-[#1A7A4A]">{item.transferIn>0?fmt(item.transferIn):'—'}</td>
                          <td className="px-4 py-3 text-right text-[#B7770D]">{item.transferOut>0?fmt(item.transferOut):'—'}</td>
                          <td className="px-4 py-3 text-right font-bold text-[#1C1A18]">{fmt(item.balance)}</td>
                          <td className="px-4 py-3 text-right text-xs text-[#6B7280]">{fmt(item.weightedAvgRate)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-amber-700">{fmt(item.stockValue)}</td>
                          <td className="px-4 py-3">
                            {item.stockStatus==='negative'
                              ?<span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full border border-red-200">Negative</span>
                              :item.stockStatus==='out'
                              ?<span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full border border-red-200">Out of Stock</span>
                              :item.stockStatus==='critical'
                              ?<span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full border border-red-200">Critical</span>
                              :item.stockStatus==='low'
                              ?<span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">Low Stock</span>
                              :<span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-200">In Stock</span>}
                            {item.stockPolicySource==='customized' && <span className="ml-1 text-[10px] text-[#9CA3AF]" title="Uses this material's customized Stock Policy">•</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Suppliers Tab */}
            {activeTab==='suppliers' && (
              <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                <div className="px-4 py-3 border-b border-[#F3F4F6]">
                  <h3 className="text-sm font-semibold text-[#2E2C29]">Supplier Analysis — Ranked by Total Received Value</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                      <tr>{['#','Supplier / Vendor','POs','Materials','Total Received (Exc.VAT)','% of Total'].map(h=>(
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase whitespace-nowrap">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {supplierAnalysis.length===0?(
                        <tr><td colSpan={6} className="px-4 py-12 text-center text-[#9CA3AF] text-sm">No supplier data</td></tr>
                      ):supplierAnalysis.map((s,idx)=>{
                        const grandTotal = supplierAnalysis.reduce((sum,x)=>sum+x.totalValue,0)
                        const pct = grandTotal > 0 ? (s.totalValue/grandTotal*100) : 0
                        return (
                          <tr key={idx} className="hover:bg-[#FAFAFA]">
                            <td className="px-4 py-3 text-[#9CA3AF] text-xs">{idx+1}</td>
                            <td className="px-4 py-3 font-medium text-[#1C1A18]">{s.vendor}</td>
                            <td className="px-4 py-3 text-center"><span className="bg-[#DDD9D0] text-[#1A7A4A] text-xs px-2 py-0.5 rounded-full">{s.poCount}</span></td>
                            <td className="px-4 py-3 text-center"><span className="bg-[#EDE7DA] text-[#6B7280] text-xs px-2 py-0.5 rounded-full">{s.materials}</span></td>
                            <td className="px-4 py-3 text-right font-bold text-[#1A7A4A]">{fmt(s.totalValue)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-[#EDE7DA] rounded-full h-2">
                                  <div className="bg-emerald-500 h-2 rounded-full" style={{width:Math.min(pct,100)+'%'}}/>
                                </div>
                                <span className="text-xs text-[#6B7280] w-10 text-right">{pct.toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot className="border-t-2 border-[#E5E7EB] bg-[#F5F1EA]">
                      <tr>
                        <td colSpan={4} className="px-4 py-3 text-xs font-bold text-[#6B7280] uppercase">Total</td>
                        <td className="px-4 py-3 text-right font-bold text-[#1A7A4A]">{fmt(supplierAnalysis.reduce((s,x)=>s+x.totalValue,0))}</td>
                        <td className="px-4 py-3 text-center text-xs text-[#6B7280]">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

          </>
        )}
      </div>

      {modal?.type==='receipt' && <ReceiptModal project={selectedProject} poLine={modal.poLine} po={modal.po} costCodes={costCodes} receipts={receipts} onSave={handleSaveReceipt} onClose={()=>setModal(null)}/>}
      {modal?.type==='issue' && <IssueModal project={selectedProject} poLine={modal.poLine} po={modal.po} costCodes={costCodes} subCodesList={subCodesList} receipts={receipts} issues={issues} onSave={handleSaveIssue} onClose={()=>setModal(null)}/>}
      {modal?.type==='bulk' && <BulkModal project={selectedProject} po={modal.po} bulkType={modal.bulkType} poLines={poLines} receipts={receipts} issues={issues} costCodes={costCodes} onSaveBulk={handleSaveBulk} onClose={()=>setModal(null)}/>}
      {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
    </div>
  )
}
